import { useState, useRef, useEffect, useMemo } from 'react';
import { nameOnChange } from '@/utils/validation';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select, SearchableSelect, ErrorSummary, BackButton, Modal } from '@/components/common';
import { VehicleDuplicateModal, renewalRefusalWords } from '@/components/policies/VehicleDuplicateModal';
// Imported from the module directly (not the `@/hooks` barrel): the check is
// advisory, and a page whose tests replace the barrel must still render.
import { useVehicleCheck } from '@/hooks/usePolicies';
import { AddressFields } from '@/components/forms/AddressFields';
import { RtoAddressFields } from '@/components/forms/RtoAddressFields';
import { useRTO } from '@/hooks/useGeography';
// The agent picker — and ONLY the agent picker — is server-driven: its option
// set is a search result from GET /brokers/posps/, not the whole directory.
// `common/SearchableSelect` always re-filters `options` client-side on the
// option LABEL, and the label is "{user_name} ({posp_code})" — so a row the
// server matched on `user__email` (a declared POSPViewSet search field) was
// fetched and then hidden, showing "No matches" for a real agent, and the
// `{value: '', label: 'Select Agent'}` clear entry disappeared as soon as any
// query was typed, making the agent impossible to detach without first
// emptying the search box. `ui/SearchableSelect` implements the server-driven
// contract: pass `onQueryChange` and it trusts `options` as-is.
import { SearchableSelect as ServerSearchableSelect } from '@/components/ui/SearchableSelect';
import { useCreatePolicy, useUpdatePolicy, useCustomers, usePolicy, usePolicyDocuments } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { useBrokerInsurers } from '@/hooks/useBrokerInsurers';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { usePolicyClassification } from '@/hooks/usePolicyClassification';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { usePOSPsLookup } from '@/hooks/useBrokers';
import { useProductsLookup, useInsuranceTypesLookup } from '@/hooks/useProducts';
import {
  useVehicleMakes,
  useVehicleModels,
  useVehicleVariants,
  useFuelTypesLookup,
} from '@/hooks/useVehicles';
import { leadService, policyService, quoteService } from '@/services';
import { useInsuranceTypeSchema, type FieldSchema } from '@/hooks/useInsuranceTypeSchema';
import { widgetForDataType } from '@/pages/policies/dynamicSchema';
import { InsuredMembersBlock } from '@/components/policies/InsuredMembersBlock';
import {
  initialMembers,
  membersFromPolicy,
  membersToPayload,
  validateMembers,
  type MemberRow,
} from '@/pages/policies/healthStructure';
import { applyServerErrors } from '@/utils/formErrors';
import { getFieldErrors } from '@/services/api';
import {
  customerTypeOptions,
  salutationCodesFor,
  isCompanyNameType,
  getBasicFields,
  getBasicFieldSpecs,
  getIdentityFields,
  getIdentityFieldSpecs,
  type CustomerFieldSpec,
} from '@/utils/customerBasicInfo';
import {
  isCheckableVehicleNumber,
  normaliseVehicleNumber,
  RENEWAL_CUSTOMER_MISMATCH,
  type BrokerContext,
  type Customer,
  type PolicyCreateRequest,
  type VehicleCheckResult,
} from '@/types';
import { PAYMENT_FREQUENCY_OPTIONS, renewalRefusalOf, type RenewalPrefill } from '@/services/policies';
import { pospOptionLabel } from './pospOptionLabel';
import { applyLastContext } from './applyLastContext';
import type { LastContext } from './applyLastContext';
import { getContactPolicyPrefill } from './contactPrefill';
import type { ContactPrefillVehicle } from './contactPrefill';

// B12 -- above this many months, `policy_term_months` gets a confirmation
// prompt on submit (see `onSubmit`'s own check), not a hard validation error.
//
// Threshold chosen at 60 (5 years): IRDAI's long-term motor mandate already
// makes bundled OWN-DAMAGE-FREE third-party terms of 36 months (new 4-wheeler)
// and 60 months (new 2-wheeler) ORDINARY, not rare, so the line has to sit
// at or above 60 or every routine new-2-wheeler policy would be nagged.
// Anything past that -- the real 2026-08-06 -> 2036-08-05 prod policy this
// item was reported against is 120 -- is rare enough to be worth a second
// look, without being blocked: long terms are legitimate (multi-year
// package/bundled covers, some life/health products), just uncommon.
const LONG_POLICY_TERM_MONTHS = 60;

// M-04 (24 Sep): the server's `installments.MAX_NUMBER_OF_PAYMENTS` -- 100
// years of monthly payments. Was 100, which refused a 20-year monthly term.
const MAX_NUMBER_OF_PAYMENTS = 1200;

const policyCreateSchema = z.object({
  // Policy Context — Insurance Type and Insurance Company are always
  // mandatory. Policy Type / Group / Subgroup are conditionally mandatory
  // (required only when master data offers options — enforced in onSubmit
  // via classificationFieldState, FIX 11).
  insurance_type: z.string().min(1, 'Select an insurance type'),
  policy_type: z.string().optional().or(z.literal('')),
  group: z.string().optional().or(z.literal('')),
  subgroup: z.string().optional().or(z.literal('')),
  insurer: z.string().min(1, 'Select an insurance company'),
  // #588: the insurer this policy follows (same as `insurer` = renewal, else rollover).
  previous_insurer: z.string().optional().or(z.literal('')),
  product: z.string().optional().or(z.literal('')),

  // Customer linkage (optional)
  customer: z.string().optional(),

  // Holder — required unless a customer is linked (validated in onSubmit)
  holder_name: z.string().max(255).optional().or(z.literal('')),
  holder_phone: z
    .string()
    .regex(/^\d{7,15}$/, 'Enter a valid phone number (7-15 digits)')
    .optional()
    .or(z.literal('')),
  holder_email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  holder_dob: z.string().optional().or(z.literal('')),
  holder_gender: z.string().optional().or(z.literal('')),
  holder_pincode: z
    .string()
    .regex(/^\d{6}$/, 'Enter a valid 6-digit pincode')
    .optional()
    .or(z.literal('')),

  // Wave E — structured Basic-Information holder fields, shown only for a
  // NEW customer (no linked customer, create mode). Mirrors the mandatory
  // subset of Contacts' per-customer_type Basic Information (Wave D1b);
  // holder_name above is still derived from these on submit and sent as the
  // Policy's own denormalized holder name.
  holder_customer_type: z.string().optional().or(z.literal('')),
  holder_salutation: z.string().optional().or(z.literal('')),
  holder_first_name: z.string().max(100).optional().or(z.literal('')),
  holder_last_name: z.string().max(100).optional().or(z.literal('')),

  // Dates
  inception_date: z.string().min(1, 'Inception date is required'),
  expiry_date: z.string().min(1, 'Expiry date is required'),
  issue_date: z.string().optional(),
  grace_period: z.string().optional(),
  policy_term_months: z.string().regex(/^\d+$/, 'Enter a valid number').optional().or(z.literal('')),

  // Premium Paying Term (Life / Health / Term). Both blank = a policy paid
  // once, which is every motor policy and the default everywhere else. The
  // pair is validated TOGETHER in onSubmit: half a setting would produce a
  // policy that looks like it is on a schedule and has none.
  payment_frequency: z.string().optional().or(z.literal('')),
  number_of_payments: z
    .string()
    .regex(/^\d{1,4}$/, 'Enter a number of payments')
    .optional()
    .or(z.literal('')),
  // B-01 (24 Sep): what EACH installment costs. Without it the server split
  // the total premium by the payment count, so a 5,000 per-installment
  // premium over 10 payments was scheduled as ten 500s.
  installment_amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),

  // Premium Details — all optional per spec
  od_premium: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  od_tax: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  tp_premium: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  tp_tax: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  net_premium: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  total_premium: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  // Item 11 — "Taxes" on a NON-motor policy. It is bound to the EXISTING
  // `Policy.gst_amount` column rather than a new one (widened 10,2 -> 12,2 in
  // policies migration 0026 so a tax can reach the magnitude of the premium it
  // taxes). Motor keeps its own OD Tax / TP Tax pair and never renders this.
  gst_amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),

  // Vehicle Details — all optional (motor policies)
  vehicle_number: z.string().max(20).optional().or(z.literal('')),
  registration_date: z.string().optional().or(z.literal('')),
  manufacturing_year: z
    .string()
    .regex(/^\d{4}$/, 'Enter a 4-digit year')
    .optional()
    .or(z.literal('')),
  ncb_percentage: z
    .string()
    .regex(/^\d{1,3}$/, 'Enter a valid NCB %')
    .optional()
    .or(z.literal('')),
  vehicle_make: z.string().optional().or(z.literal('')),
  vehicle_model: z.string().optional().or(z.literal('')),
  vehicle_variant: z.string().optional().or(z.literal('')),
  // B1 — FK id from the Country-Admin-managed vehicles.VehicleFuelType master.
  fuel_type: z.string().optional().or(z.literal('')),

  // B2 — Coverage amount. IDV is the motor form of it, Sum Assured every other
  // type's; both are optional and validated like the premium amounts above.
  idv: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),
  sum_insured: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount').optional().or(z.literal('')),

  // Agent — mandatory only for motor policies (enforced in onSubmit)
  posp: z.string().optional().or(z.literal('')),

  // Queue #218: engine capacity (every motor policy) and gross vehicle weight
  // (commercial vehicles only). Both explicitly NOT mandatory. Whether each is
  // rendered is decided by the InsuranceTypeFieldSchema registry, not here --
  // see the Vehicle Details card below. Their values ride into `policy_data`
  // through the existing schema-driven fold in onSubmit, because Policy has no
  // column for either.
  cc: z.string().regex(/^\d+$/, 'Enter a whole number').optional().or(z.literal('')),
  // B-04: the vehicle's Engine No. and Chassis No. Optional. Policy has no
  // column for either, so like cc/gvw they ride into `policy_data`, and the
  // server copies them onto the customer's vehicle -- the Vehicle Database.
  engine_number: z.string().max(50, 'At most 50 characters').optional().or(z.literal('')),
  chassis_number: z.string().max(50, 'At most 50 characters').optional().or(z.literal('')),
  gvw: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid weight').optional().or(z.literal('')),

  // Board #199 — whether this is a brand-new vehicle or an already-registered
  // one. Optional, and a commission-rate criterion rather than a Policy column:
  // it rides into `policy_data`, which is where the engine reads it from.
  // Defaulted from the registration date against the policy start and
  // overridable, never guessed silently.
  vehicle_condition: z.enum(['new', 'old']).optional().or(z.literal('')),

  // #333: optional motor extras with no Policy column. All of them ride in ONE
  // `policy_data.motor_optional` object (a single key, so the edit-mode reset()
  // has one thing to restore, not twelve), and none is ever required.
  motor_optional: z
    .object({
      previous_insurer: z.string().max(150).optional(),
      previous_policy_number: z.string().max(60).optional(),
      previous_expiry_date: z.string().optional(),
      previous_year_claim: z.enum(['yes', 'no', '']).optional(),
      ncb_proof: z.string().max(150).optional(),
      pa_owner_driver: z.enum(['yes', 'no', '']).optional(),
      financier_name: z.string().max(255).optional(),
      addon_covers: z.string().max(500).optional(),
      seating_capacity: z.string().regex(/^\d{0,3}$/, 'Enter a whole number').optional(),
      discount_percentage: z
        .string()
        .regex(/^(\d{1,2}(\.\d{1,2})?|100(\.0{1,2})?)?$/, 'Enter 0 to 100')
        .optional(),
      loading_percentage: z
        .string()
        .regex(/^(\d{1,3}(\.\d{1,2})?)?$/, 'Enter a valid percentage')
        .optional(),
      payment_reference: z.string().max(100).optional(),
    })
    .optional(),
  // #333: one optional nominee. Create only -- a nominee is its own row
  // (PolicyNominee), and the update endpoint does not accept nested nominees.
  nominee_name: z.string().max(255).optional().or(z.literal('')),
  nominee_relationship: z.string().optional().or(z.literal('')),
  nominee_share: z
    .string()
    .regex(/^(100|\d{1,2})(\.\d{1,2})?$/, 'Enter a share from 0 to 100')
    .optional()
    .or(z.literal('')),

  // M-04 — the RTO block a NEW first registration shows in place of Vehicle
  // Number and Registration Date. A vehicle that has not been registered yet
  // has neither, but it does have the office it is about to be registered at,
  // and `rto_id` is a Motor commission rate dimension the engine already reads
  // out of `policy_data` (apps.commissions.services._motor_facts).
  //
  // Only `rto_id` and `rto_pincode` are saved. State and City exist to narrow
  // the RTO list; the chosen office names its own state and city, so storing
  // them again would just be a second copy that can disagree. They are
  // rehydrated from the RTO itself on edit.
  //
  // RTO stays OPTIONAL (decision 13): none of these four is ever required.
  rto_id: z.string().optional().or(z.literal('')),
  rto_state: z.string().optional().or(z.literal('')),
  rto_city: z.string().optional().or(z.literal('')),
  rto_pincode: z
    .string()
    .regex(/^\d{6}$/, 'Enter a 6-digit PIN code')
    .optional()
    .or(z.literal('')),

  // "Policy Number" the broker enters is stored as insurer_policy_number and
  // shown as the policy's number; the system policy_number is generated and
  // kept internally. Queue #218 made it mandatory on ADD -- enforced in
  // onSubmit rather than here, because it must NOT apply in edit mode: roughly
  // 200k existing policies predate the field, and requiring it in the schema
  // would make every one of them unsaveable until someone found its number.
  insurer_policy_number: z.string().optional(),
  notes: z.string().optional(),
  // #332: group health master policy (real Policy columns). The other
  // registry-driven health fields (deductible, portability, ...) are registered
  // by key at render time and read back with getValues() in onSubmit, because
  // this schema cannot name keys the registry decides.
  master_policy_number: z.string().max(100).optional().or(z.literal('')),
  master_policy_name: z.string().max(255).optional().or(z.literal('')),
});

type PolicyCreateFormData = z.infer<typeof policyCreateSchema>;

/**
 * What "the broker emptied this box" must be SENT as, per column, on an edit.
 *
 * The submit loop drops every empty value. On a create that is right — an
 * absent key takes the column's default. On an edit it is a PATCH, where an
 * absent key means "do not touch this column", so a cleared box saved the OLD
 * figure and the page still said "Policy updated successfully". Clearing Taxes
 * was the damaging case: `gst_amount` vanished from the request while the
 * derivation still posted a `total_premium` computed WITHOUT that tax, leaving
 * a row whose gst was no longer total minus net.
 *
 * The value is decided by the column, not by taste (apps/policies/models.py):
 *   - `'0'` for the NOT NULL DecimalField(default=0) amounts, where zero is
 *     the only way the column can say "none". `null` is a 400 on these.
 *   - `null` for the genuinely nullable cover amounts, where 0 would be a
 *     claim that the policy covers nothing rather than "not recorded".
 *
 * Deliberately NOT here:
 *   - `total_premium` / `gross_premium` — never user-editable (derived,
 *     read-only). The gross/net/total fallbacks below fill them on a CREATE;
 *     on an edit they are deliberately left out of the PATCH, because a key
 *     this page invents is not the broker's figure to overwrite (B5 — see the
 *     long note on the `isEdit` guard around those fallbacks).
 *   - `policy_term_months` — NOT NULL default 12, so a cleared box has no
 *     honest wire value: sending 12 would invent a term the broker did not
 *     type. It stays a no-op and wants a validation message instead.
 *   - `number_of_payments` — already handled explicitly with
 *     `payment_frequency`, and half a schedule is refused in onSubmit.
 */
const CLEARED_AMOUNT_ON_EDIT = new Map<string, unknown>([
  // NOT NULL DecimalField(default=0) / IntegerField(default=0).
  ['od_premium', '0'],
  ['od_tax', '0'],
  ['tp_premium', '0'],
  ['tp_tax', '0'],
  ['gst_amount', '0'],
  ['net_premium', '0'],
  ['ncb_percentage', 0],
  // Nullable columns — null is "not recorded".
  ['idv', null],
  ['previous_insurer', null],
  ['sum_insured', null],
  ['manufacturing_year', null],
]);

// Wave E: the Policy Holder form's single Name input doubles as the
// identifying name for every customer_type -- label it to match what it
// actually captures for company-name types (mirrors CustomerFormPage's
// per-type 'company_name' field labels), "First Name" otherwise.
/**
 * Read one key out of a saved policy's `policy_data` bag. Fields with no Policy
 * column of their own (Board #199's `vehicle_condition`, and the schema-driven
 * type-specific fields) live only there, so edit mode has to load them from it.
 */
function policyDataValue(policy: Record<string, unknown>, key: string): string {
  const bag = policy.policy_data;
  if (!bag || typeof bag !== 'object') return '';
  const value = (bag as Record<string, unknown>)[key];
  return value === null || value === undefined ? '' : String(value);
}

/** One key of the saved policy's PolicyVehicle (`vehicle_details`), or ''. */
function vehicleDetailValue(policy: Record<string, unknown>, key: string): string {
  const details = policy.vehicle_details;
  if (!details || typeof details !== 'object') return '';
  const value = (details as Record<string, unknown>)[key];
  return value === null || value === undefined ? '' : String(value);
}

// A blank option, not just the shared `Select`'s placeholder: that placeholder
// is rendered as a DISABLED option, so without this the broker could never put
// an optional answer back to unanswered once they had picked one.
const yesNoOptions = [
  { value: '', label: 'Not stated' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/** First saved nominee's value for the nominee box (edit mode). */
function savedNominee(policy: Record<string, unknown>, key: string): string {
  const list = policy.nominees;
  if (!Array.isArray(list) || list.length === 0) return '';
  const v = (list[0] as Record<string, unknown>)[key];
  return v === null || v === undefined ? '' : String(v);
}

/** Registry keys the page already renders with its own control. */
const HEALTH_OWN_CONTROL_KEYS = new Set(['sum_insured', 'cover_type', 'grace_period_days']);

const nomineeRelationshipOptions = [
  { value: '', label: 'Select relationship' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other' },
];

/** The saved `policy_data.motor_optional` bag as the form's string values. */
function savedMotorOptional(policy: Record<string, unknown>): Record<string, string> {
  const bag = policy.policy_data;
  const raw = bag && typeof bag === 'object' ? (bag as Record<string, unknown>).motor_optional : null;
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v !== null && v !== undefined) out[k] = String(v);
  }
  return out;
}

const vehicleConditionOptions = [
  { value: '', label: 'Not specified' },
  { value: 'new', label: 'New — first registration' },
  { value: 'old', label: 'Old — already registered' },
];

/**
 * A saved `vehicle_condition`, narrowed to what the form accepts. Anything the
 * bag holds that is not 'new' or 'old' reads as unanswered rather than being
 * forced into one of them — the value drives commission matching, so a
 * mis-coerced answer would silently pick a different rate.
 */
function savedVehicleCondition(policy: Record<string, unknown>): '' | 'new' | 'old' {
  const value = policyDataValue(policy, 'vehicle_condition');
  return value === 'new' || value === 'old' ? value : '';
}

// ── Board #272: the new holder IS a Contact ────────────────────────────────
// Add Policy used to capture four holder fields and the backend filled the
// rest of the Contact with placeholders (address "—", pincode "000000", no
// PAN, no DOB, no GST). The operator answered a form, got a 201, and then had
// to reopen the contact in Contacts and type it all again.
//
// The Basic Information and Identity & Tax blocks below are rendered from
// `getBasicFieldSpecs` / `getIdentityFieldSpecs` -- the SAME per-customer_type
// schema the Contacts form renders from (utils/customerBasicInfo.ts), imported
// rather than restated so the two forms cannot drift into asking a company for
// a date of birth in one place and not the other. The whole block is posted as
// `new_customer`, which `PolicyCreateSerializer` validates with
// `CustomerCreateSerializer` and creates inside the policy's own transaction.

/** The address/contact fields Contacts collects outside the per-type schema. */
const NEW_CONTACT_ADDRESS_FIELDS: { key: string; label: string; maxLength?: number; inputMode?: 'numeric' }[] = [
  { key: 'address_line1', label: 'Address Line 1' },
  { key: 'address_line2', label: 'Address Line 2' },
  { key: 'landmark', label: 'Landmark' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode', maxLength: 6, inputMode: 'numeric' },
];

/** Every key the nested block may carry, for the current customer type. */
function newContactKeys(customerType: string): string[] {
  return [
    ...getBasicFields(customerType),
    ...getIdentityFields(customerType),
    ...NEW_CONTACT_ADDRESS_FIELDS.map((field) => field.key),
  ];
}

// Task 8 (board): a date of birth and a gender belong to a NATURAL PERSON, so
// the Policy Holder card stops asking an entity for them. Corporate / HUF /
// Trust / Partnership / LLP holders have an incorporation date, not a birthday.
//
// Deliberately NOT isCompanyNameType() (corporate + proprietorship): that
// helper answers "is the identifying name a company name", which is a
// different question, and the two sets genuinely differ on proprietorship. A
// proprietorship IS its proprietor in law, and Contacts' own per-type config
// (CustomerFormPage's BASIC_FIELDS_BY_TYPE) keeps date_of_birth + gender for
// proprietorship for exactly that reason -- the two forms must not disagree
// about the same person.
//
// HUF/Trust/Partnership/LLP fall back to the INDIVIDUAL field set in Contacts
// today, but that is a documented Phase-2 placeholder, not a claim that a trust
// has a birthday. When Phase 2 gives those types a real config, reconcile this
// list with BASIC_FIELDS_BY_TYPE -- ideally by moving this helper next to
// isCompanyNameType in utils/customerBasicInfo.ts, which is where it belongs
// once both forms need it.
//
// An unknown or blank type counts as a natural person, so the ~200k policies
// written before holder_customer_type existed keep the form they always had.
const NON_NATURAL_PERSON_TYPES = ['corporate', 'huf', 'trust', 'partnership', 'llp'];

function isNaturalPersonType(customerType: string | undefined): boolean {
  return !NON_NATURAL_PERSON_TYPES.includes(customerType ?? '');
}

// B1 (15 Sep QA batch): Premium Paying Term / instalments are a Health, Life
// or Term feature ("Investment" counts as Life) -- an ALLOW list matching the
// server's `installments_refused_reason` (apps/policies/installments.py),
// keyed on the insurance type's CODE, never its display name. Country admins
// have created ad-hoc types with mixed-case codes ("Investment", "Cyber") and
// one with a space in it ("Sports Insurance"), so this always normalises
// before comparing.
const PPT_ALLOWED_TYPE_CODES = new Set(['health', 'life', 'term', 'investment']);

function supportsPremiumPayingTerm(code: string | undefined): boolean {
  return !!code && PPT_ALLOWED_TYPE_CODES.has(code.trim().toLowerCase());
}

export function PolicyCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = !!editId;
  const { data: existingPolicy } = usePolicy(editId ?? '');
  const { data: existingDocs } = usePolicyDocuments(editId ?? '');
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer') || '';
  // B3: "Convert to Policy" on a completed quote links here with ?quote=<id>,
  // but nothing read the parameter -- it was discarded, so the form opened
  // blank and every customer, quote and vehicle detail had to be retyped.
  const preselectedQuote = searchParams.get('quote') || '';
  // Creating a policy straight off a lead. The lead already holds the
  // customer AND (for motor) the vehicle; without this the broker re-typed
  // both from the lead page they had just been reading.
  const preselectedLead = searchParams.get('lead') || '';
  // What the SOURCE RECORD said, for the make/model/variant its free text
  // could not be resolved onto the vehicle master. Shown as a hint under the
  // blank select so the broker is not left guessing what was actually
  // recorded. `source` names where it came from -- item 2 gave contacts the
  // same prefill leads already had, and "Lead recorded:" on a contact's car
  // would be a lie.
  const [prefillVehicleText, setPrefillVehicleText] = useState({
    source: 'Lead',
    make: '',
    model: '',
    variant: '',
    fuel: '',
  });
  // B11 second half: what the SOURCE RECORD said the enquiry was about, for
  // the product line its text could not be matched onto an InsuranceType.
  // Shown under the blank select for the same reason the vehicle hint is --
  // the server refuses to guess a classification, because the wrong one
  // silently reprices the policy against a different commission rate card.
  const [prefillCoverText, setPrefillCoverText] = useState({ source: 'Lead', insuranceType: '' });
  // Item 2 -- the contact's vehicles, from GET /customers/{id}/policy-prefill/.
  // A contact can own several; the form ASKS which one rather than taking the
  // first, because taking the first silently writes the wrong vehicle onto a
  // policy and nothing on screen says so.
  const [contactVehicles, setContactVehicles] = useState<ContactPrefillVehicle[]>([]);
  const [chosenContactVehicleId, setChosenContactVehicleId] = useState('');
  // A prefill that fails must SAY so. Left silent (the shape the lead and
  // quote prefills use) the broker sees an empty vehicle block and cannot tell
  // "this contact has no vehicle on file" from "the lookup broke" -- so they
  // trust an empty form that is merely uninformed.
  const [contactPrefillFailed, setContactPrefillFailed] = useState(false);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('policy_copy');
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── B9: renewal link + vehicle duplicate check (create mode only) ─────────
  // `?renewal=<policy id>` ("Record renewal policy" on a policy) pre-sets the
  // link and skips the prompt. Otherwise the Vehicle Number is checked on blur
  // and again at submit; a live policy on the same vehicle raises the "Create
  // as Renewal / Create as New Policy Anyway" prompt. The link is held in a
  // ref as well as state because a modal choice resubmits straight away,
  // before a re-render could hand onSubmit the new value.
  const renewalParam = searchParams.get('renewal') || '';
  const [renewalLink, setRenewalLinkState] = useState<{ id: string; displayNumber: string } | null>(null);
  const renewalLinkRef = useRef<{ id: string; displayNumber: string } | null>(null);
  // A refusal (from the prefill's eligibility or the save) shown above the form.
  const [renewalNotice, setRenewalNotice] = useState('');
  const [vehicleCheckResult, setVehicleCheckResult] = useState<VehicleCheckResult | null>(null);
  // "Could not check" is not "no duplicates": said under the input, never
  // blocking -- the server does not refuse duplicates either.
  const [vehicleCheckFailed, setVehicleCheckFailed] = useState(false);
  // The normalised number already cleared (no live match, or "New Policy
  // Anyway" chosen), so it is not asked about twice.
  const clearedVehicleRef = useRef('');
  const inflightCheckRef = useRef<{ norm: string; promise: Promise<VehicleCheckOutcome> } | null>(null);
  // Set when the prompt interrupted a submit, so the choice carries the submit on.
  const resumeSubmitRef = useRef(false);
  // The broker confirmed linking a policy that belongs to another customer --
  // held as `<renewed policy id>:<customer key>`, so the "yes" covers exactly
  // the pairing that was asked about. Choosing another customer asks again.
  const ackMismatchKeyRef = useRef('');
  // The pairing of the submit the server just refused, awaiting the answer.
  const pendingMismatchKeyRef = useRef('');
  const [mismatchMessage, setMismatchMessage] = useState<string | null>(null);
  // B12 -- an unusually long policy term (see LONG_POLICY_TERM_MONTHS below)
  // gets a confirmation, not a hard block: a real prod policy runs ten years
  // (2026-08-06 -> 2036-08-05), so the number alone cannot be treated as a
  // mistake. Holds the exact `policy_term_months` string the broker has
  // already confirmed, the same "remember which ANSWER, not just that some
  // answer was given" shape as `ackMismatchKeyRef` above: resubmitting the
  // SAME confirmed value does not ask again, but changing it to a different
  // (still long) number does -- the earlier "yes" was about the number that
  // was on screen then, not a blanket "always trust me on this field".
  const ackLongTermMonthsRef = useRef('');
  const [longTermConfirmMonths, setLongTermConfirmMonths] = useState<string | null>(null);
  const vehicleCheck = useVehicleCheck();

  const { user } = useAuthStore();
  // The BrokerSwitcher's selection, falling back to the token's pinned tenant.
  // A broker-side user can belong to several brokers while `user.tenant_id`
  // names exactly one of them and switching does not change it, so reading
  // `tenant_id` alone filters by the wrong tenant the moment anyone switches.
  // Same expression the commissions page uses for its running balance.
  const brokerLoginType = useAuthStore((s) => s.loginType);
  const brokerTenantContext = useAuthStore((s) => s.tenantContext);
  const brokerId =
    (brokerLoginType === 'broker'
      ? (brokerTenantContext as BrokerContext | null)?.id ?? undefined
      : undefined) ?? (user?.tenant_type === 'broker' ? user.tenant_id ?? undefined : undefined);

  // `useBrokerInsurers`, NOT `useBrokerInsuranceCompanies`: that hook takes a
  // brokerId and ignores it (`_brokerId`), so its cache key is always 'me' and
  // the dropdown kept the first broker's insurers after a switch.
  const { data: brokerInsurers, isLoading: insurersLoading } = useBrokerInsurers(brokerId);
  // Task 3 (board): the agent picker only ever held PAGE ONE of the agent list
  // (20 rows), and SearchableSelect filters client-side over whatever it was
  // handed — so with 27 active agents the last 7 could not be selected at all.
  // Typing now drives the endpoint's own search, debounced, exactly like the
  // customer picker below.
  const { debouncedTerm: pospSearch, setTerm: setPospSearch } = useDebouncedSearch();
  const { data: posps, isLoading: pospsLoading } = usePOSPsLookup(pospSearch || undefined);
  // BROKER-2a: the customer picker was capped at the first page of 20 (the
  // SearchableSelect only filtered that slice client-side). Drive a debounced
  // server-side search and fetch a wider slice (page_size 100) so any customer
  // is reachable by typing, not just the first 20.
  const { debouncedTerm: customerSearch, setTerm: setCustomerSearch } = useDebouncedSearch();
  const { data: initialCustomers, isLoading: customersLoading } = useCustomers(1, {
    search: customerSearch || undefined,
    page_size: 100,
  });
  // Memoised, not a bare `?? []`: the fallback allocates a NEW empty array on
  // every render, and this value is a dependency of the pin effect below. An
  // unstable dependency there re-runs the effect on every render, which is the
  // same class of defect that made the agent pin spin forever (see the
  // functional setPinnedPosp update). react-hooks/exhaustive-deps flags it.
  const customerList = useMemo(() => initialCustomers?.results ?? [], [initialCustomers]);
  // Keep the currently-selected customer visible even when it falls outside the
  // latest (search-narrowed) results, so selecting then typing a new query
  // doesn't make the chosen customer's label vanish. Pin the last-seen object.
  const [pinnedCustomer, setPinnedCustomer] = useState<Customer | null>(null);
  // Same idea for the agent: remember the picked option so a later, narrower
  // search can't make its label disappear (see pospOptions below).
  const [pinnedPosp, setPinnedPosp] = useState<{ value: string; label: string } | null>(null);

  const createMutation = useCreatePolicy();
  const updateMutation = useUpdatePolicy();
  // Belt-and-suspenders double-submit guard: the button's disabled/isLoading
  // state (bound to the mutation's isPending below) covers the normal case,
  // but a rapid double-click can fire a second synchronous handleSubmit call
  // before React re-renders the disabled attribute. This ref is checked and
  // set synchronously at the top of onSubmit, so the second call returns
  // immediately regardless of render timing.
  const isSubmittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<PolicyCreateFormData>({
    resolver: zodResolver(policyCreateSchema),
    defaultValues: {
      insurance_type: '',
      policy_type: '',
      group: '',
      subgroup: '',
      insurer: '',
      previous_insurer: '',
      product: '',
      customer: preselectedCustomer,
      holder_name: '',
      holder_phone: '',
      holder_email: '',
      holder_dob: '',
      holder_gender: '',
      holder_pincode: '',
      holder_customer_type: 'individual',
      holder_salutation: '',
      holder_first_name: '',
      holder_last_name: '',
      inception_date: '',
      expiry_date: '',
      issue_date: '',
      grace_period: '',
      policy_term_months: '',
      payment_frequency: '',
      number_of_payments: '',
      installment_amount: '',
      od_premium: '',
      od_tax: '',
      tp_premium: '',
      tp_tax: '',
      net_premium: '',
      total_premium: '',
      gst_amount: '',
      vehicle_number: '',
      registration_date: '',
      manufacturing_year: '',
      ncb_percentage: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_variant: '',
      fuel_type: '',
      idv: '',
      sum_insured: '',
      posp: '',
      insurer_policy_number: '',
      cc: '',
      gvw: '',
      engine_number: '',
      chassis_number: '',
      vehicle_condition: '',
      motor_optional: {},
      nominee_name: '',
      nominee_relationship: '',
      nominee_share: '',
      rto_id: '',
      rto_state: '',
      rto_city: '',
      rto_pincode: '',
      notes: '',
    },
  });

  const selectedInsuranceType = watch('insurance_type');
  const selectedInsurer = watch('insurer');
  const selectedGroup = watch('group');
  const selectedProduct = watch('product');
  const selectedMake = watch('vehicle_make');
  const selectedModel = watch('vehicle_model');
  // Watched (not just registered) because the three vehicle selects are bound
  // to the form value — see the option memos below (B4).
  const selectedVariant = watch('vehicle_variant');
  const selectedFuelType = watch('fuel_type');
  const selectedCustomerId = watch('customer');
  // Watched (not read inline in the JSX) because the agent option list is
  // built from it — see pospOptions below.
  const selectedPospId = watch('posp') || '';
  const odPremium = watch('od_premium');
  const odTax = watch('od_tax');
  const tpPremium = watch('tp_premium');
  const tpTax = watch('tp_tax');
  // Item 11 — the non-motor pair. Net Premium is typed by the broker for these
  // types (motor derives it from OD + TP), and Total Premium is Net + Taxes.
  const netPremium = watch('net_premium');
  const taxes = watch('gst_amount');
  const inceptionDate = watch('inception_date');
  const expiryDate = watch('expiry_date');
  const selectedCustomer =
    customerList.find((c) => c.id === selectedCustomerId) ||
    (pinnedCustomer && pinnedCustomer.id === selectedCustomerId ? pinnedCustomer : undefined);
  const holderCustomerType = watch('holder_customer_type') || 'individual';

  // Remember the selected customer object once it appears in a result set, so it
  // stays pinned into the dropdown/label after the query narrows the list.
  useEffect(() => {
    if (!selectedCustomerId) {
      setPinnedCustomer(null);
      return;
    }
    const found = customerList.find((c) => c.id === selectedCustomerId);
    if (found) setPinnedCustomer(found);
  }, [customerList, selectedCustomerId]);

  // Same for the agent: usePOSPsLookup replaces its results wholesale on every
  // new search term, so remember the picked agent's label while it is still in
  // view. Without this, picking an agent and then typing anything else in the
  // box blanks the closed field — see the pospOptions comment below.
  useEffect(() => {
    if (!selectedPospId) {
      setPinnedPosp(null);
      return;
    }
    const found = (posps || []).find((p) => p.id === selectedPospId);
    if (!found) return;
    const label = pospOptionLabel(found);
    // Functional update that returns the SAME object when nothing changed:
    // `posps` is a fresh array on every query result, so this effect re-runs
    // constantly, and storing a new {value,label} literal each time would
    // re-render, re-run the effect, and spin forever.
    setPinnedPosp((previous) =>
      previous && previous.value === found.id && previous.label === label
        ? previous
        : { value: found.id, label }
    );
  }, [posps, selectedPospId]);

  // Wave E: the structured Basic-Information holder subset only applies when
  // creating a policy for a brand-new customer. A linked customer keeps the
  // pre-existing pre-filled Holder Name/Phone/Email(/DOB/Gender/Pincode)
  // fields untouched (per the spec's guardrail), and edit mode never
  // auto-creates a Contact (PolicyCreateSerializer.validate() gates
  // _auto_create_contact on self.instance is None) so it keeps showing the
  // same legacy fields it always has.
  const useStructuredHolderFields = !isEdit && !selectedCustomer;

  // ── B9 helpers ────────────────────────────────────────────────────────────
  const setRenewalLink = (link: { id: string; displayNumber: string } | null) => {
    renewalLinkRef.current = link;
    setRenewalLinkState(link);
    // An acknowledgement was about ONE pairing; a different link must ask again.
    ackMismatchKeyRef.current = '';
  };

  /**
   * Load the policy being renewed: its display number for the banner, whether
   * it can be renewed at all, and the insurer / vehicle number for fields the
   * broker has left EMPTY. The customer is deliberately not filled: the picker
   * only pins a contact it has fetched, and an unpinned id would post both a
   * linked customer and a new-contact block.
   */
  const fillFromRenewalSource = async (sourceId: string, { refuseIneligible }: { refuseIneligible: boolean }) => {
    let prefill: RenewalPrefill;
    try {
      prefill = await policyService.getRenewalPrefill(sourceId);
    } catch {
      if (refuseIneligible) {
        setRenewalNotice('Could not load the policy being renewed. The link is still checked when you save.');
      }
      return;
    }
    // Undone (or replaced) while the request was in flight.
    if (renewalLinkRef.current?.id !== sourceId) return;
    const eligibility = prefill?.eligibility;
    if (refuseIneligible && eligibility && !eligibility.eligible) {
      setRenewalLink(null);
      setRenewalNotice(eligibility.message || renewalRefusalWords(eligibility.code));
      return;
    }
    let display = prefill?.prior_policy?.previous_policy_number || '';
    let insurerId = prefill?.prior_policy?.previous_insurer_id || '';
    let registration = prefill?.vehicle?.registration_number || '';
    // `prior_policy` / `vehicle` exist only for a policy with a PolicyVehicle
    // row -- almost none on the live book (found: false). Read the renewed
    // policy itself for whatever the prefill could not say.
    if (!display || !insurerId || !registration) {
      try {
        const source = await policyService.getPolicy(prefill?.policy_id || sourceId);
        if (renewalLinkRef.current?.id !== sourceId) return;
        display = display || source?.insurer_policy_number || source?.policy_number || '';
        insurerId = insurerId || source?.insurer || '';
        if (!registration && isCheckableVehicleNumber(normaliseVehicleNumber(source?.vehicle_number))) {
          registration = source.vehicle_number;
        }
      } catch {
        // Only courtesy fills are lost: the link itself is checked on save.
      }
    }
    if (renewalLinkRef.current?.id !== sourceId) return;
    if (display && !renewalLinkRef.current.displayNumber) {
      const keepAck = ackMismatchKeyRef.current;
      setRenewalLink({ id: sourceId, displayNumber: display });
      ackMismatchKeyRef.current = keepAck;
    }
    if (insurerId && !getValues('insurer')) setValue('insurer', insurerId, { shouldValidate: true });
    // #588: the policy being renewed names the insurer this one follows.
    if (insurerId && !getValues('previous_insurer')) setValue('previous_insurer', insurerId);
    if (registration && !getValues('vehicle_number')) setValue('vehicle_number', registration);
  };

  useEffect(() => {
    if (isEdit || !renewalParam) return;
    setRenewalLink({ id: renewalParam, displayNumber: '' });
    void fillFromRenewalSource(renewalParam, { refuseIneligible: true });
  }, [isEdit, renewalParam]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Ask the server whether this vehicle already has a live policy. De-duplicated
   * per number, so the blur that a click on "Add Policy" causes and the submit
   * itself share one request (and one prompt).
   */
  const runVehicleCheck = (raw: string | undefined): Promise<VehicleCheckOutcome> => {
    if (isEdit || renewalLinkRef.current) return Promise.resolve('clear');
    const norm = normaliseVehicleNumber(raw);
    if (!isCheckableVehicleNumber(norm) || norm === clearedVehicleRef.current) {
      return Promise.resolve('clear');
    }
    if (inflightCheckRef.current?.norm === norm) return inflightCheckRef.current.promise;
    // Deferred a microtask so the in-flight entry exists before the body runs.
    const promise = Promise.resolve().then(async (): Promise<VehicleCheckOutcome> => {
      // The broker may have corrected the number while this was in flight; an
      // answer about a number no longer in the box must not prompt (or link).
      const superseded = () => normaliseVehicleNumber(getValues('vehicle_number')) !== norm;
      try {
        const result = await vehicleCheck.mutateAsync({ vehicleNumber: norm });
        if (superseded()) return 'stale';
        setVehicleCheckFailed(false);
        if (!result?.checked || !result.matches?.length) {
          clearedVehicleRef.current = norm;
          return 'clear';
        }
        setVehicleCheckResult(result);
        return 'prompted';
      } catch {
        if (superseded()) return 'stale';
        setVehicleCheckFailed(true);
        return 'failed';
      } finally {
        if (inflightCheckRef.current?.norm === norm) inflightCheckRef.current = null;
      }
    });
    inflightCheckRef.current = { norm, promise };
    return promise;
  };

  const onVehicleNumberBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isEdit) return;
    void runVehicleCheck(event.target.value);
  };

  // Board #272. Held outside react-hook-form because the field SET is decided
  // at runtime by the customer type (getBasicFieldSpecs), not by a static zod
  // schema -- and because the whole object is posted as one nested key rather
  // than as flat policy columns.
  const [newContact, setNewContact] = useState<Record<string, string>>({});
  const setContactField = (key: string, value: string) =>
    setNewContact((prev) => ({ ...prev, [key]: value }));
  const basicFieldSpecs = useMemo(
    () => (useStructuredHolderFields ? getBasicFieldSpecs(holderCustomerType) : []),
    [useStructuredHolderFields, holderCustomerType]
  );
  const identityFieldSpecs = useMemo(
    () => (useStructuredHolderFields ? getIdentityFieldSpecs(holderCustomerType) : []),
    [useStructuredHolderFields, holderCustomerType]
  );
  const contactValue = (key: string) => (newContact[key] ?? '').trim();
  // The holder name the POLICY stores. Company types are identified by their
  // company name; everyone else by first + last. Mirrors
  // `PolicyCreateSerializer._holder_fields_from_new_customer` exactly -- if
  // these two disagree, the policy row and the contact it links to carry
  // different names for the same person.
  const newContactHolderName = isCompanyNameType(holderCustomerType)
    ? contactValue('company_name') || contactValue('first_name')
    : [contactValue('first_name'), contactValue('last_name')].filter(Boolean).join(' ');

  // Shared cascading classification (FIX 11): Insurance Type → Policy Type /
  // Group → Subgroup with conditional-mandatory state per field.
  const classification = usePolicyClassification(
    selectedInsuranceType || undefined,
    selectedGroup || undefined
  );

  // Type-specific fields, premium bases and rate dimensions are data-driven via
  // the InsuranceTypeFieldSchema registry rather than hardcoded motor regexes.
  const schema = useInsuranceTypeSchema(
    selectedInsuranceType || undefined,
    selectedGroup || undefined,
    watch('subgroup') || undefined,
    'policy',
    watch('product') || undefined,
  );
  const schemaFields = schema.data?.fields ?? [];
  // #332: the member structure is resolved by the server (product setting,
  // then group), so the page never hardcodes group codes.
  const memberStructure = schema.data?.memberStructure ?? null;
  // Registry rows rendered generically on a structured (health) form. The old
  // `cover_type` row is hidden once a structure resolves, so two controls
  // cannot disagree about individual vs floater.
  const registryFields = memberStructure
    ? schemaFields.filter(
        (f) => f.section !== 'vehicle' && f.section !== 'members' && !HEALTH_OWN_CONTROL_KEYS.has(f.key)
      )
    : [];
  const masterFields = registryFields.filter((f) => f.section === 'master');
  const coverageFields = registryFields.filter((f) => f.section !== 'master');
  // The server requires members for individual/floater health, so a form whose
  // schema has not resolved must not be saveable: it would silently omit them.
  const schemaPending = !!selectedInsuranceType && (schema.isLoading || schema.isError);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [memberErrors, setMemberErrors] = useState<Record<string, string>>({});
  const membersLoadedFor = useRef<string | null>(null);
  // Spec Q8: an existing health policy with no saved members stays as it is.
  // Until the broker touches the member rows (or types a master policy), an
  // edit sends no `members` key and checks none of it.
  const [membersTouched, setMembersTouched] = useState(false);
  const isMotorPolicy = schemaFields.some((f) => f.section === 'vehicle');
  // Queue #218: does the registry advertise this key for the CURRENT
  // type/group/subgroup scope? `schemaFields` is already refetched whenever any
  // of the three changes, so this follows the group selection automatically.
  const schemaOffers = (key: string) => schemaFields.some((f) => f.key === key);
  const isGracePeriodType = schemaFields.some((f) => f.key === 'grace_period_days');

  // ── Premium Paying Term (Life / Health / Term / Investment) ───────────
  // B1 (15 Sep QA batch): shown ONLY when the selected insurance type's CODE
  // is health, life, term or investment -- exactly the allow list the server
  // enforces in `installments_refused_reason`. `!isMotorPolicy` used to gate
  // this and was too broad: it let travel, home, sme and every ad-hoc type
  // (Cyber, Pet, Professional, Sports Insurance, ...) through too, because
  // `isMotorPolicy` only tests for a 'vehicle' field-schema section. Reusing
  // `usePolicyClassification`'s own lookup query (same queryKey, so this does
  // not add a network round trip) rather than gating on `isGracePeriodType`,
  // which would hide the fields for an allowed type a country admin has not
  // seeded a field schema for yet.
  const insuranceTypesLookup = useInsuranceTypesLookup();
  const selectedInsuranceTypeCode = insuranceTypesLookup.data?.find(
    (t) => t.id === selectedInsuranceType
  )?.code;
  const supportsInstallments = supportsPremiumPayingTerm(selectedInsuranceTypeCode);
  const watchedPaymentFrequency = watch('payment_frequency');
  const watchedNumberOfPayments = watch('number_of_payments');
  // Read directly rather than reusing `watchedInceptionDate` below: that const
  // is declared further down this component and would be in the temporal dead
  // zone here.
  const pptInceptionDate = watch('inception_date');

  // What the frequency and the count ACTUALLY add up to, shown back so the
  // broker can see that "quarterly x 40" ends in ten years and correct the
  // count if that is not what they meant. `null` = cannot be computed, which
  // renders as a hint rather than a made-up date.
  const premiumPayingTermEnd = useMemo(() => {
    const months = PAYMENT_FREQUENCY_OPTIONS.find((o) => o.value === watchedPaymentFrequency)?.months;
    const count = parseInt(watchedNumberOfPayments || '', 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pptInceptionDate ?? '');
    if (!months || !count || Number.isNaN(count) || !m) return null;
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    const total = (mo - 1) + months * count;
    const endYear = y + Math.floor(total / 12);
    const endMonth = (total % 12) + 1;
    // Clamp to the end of the month, matching the server's `add_months`:
    // 31 Jan + 1 month is 28 Feb, not 3 March.
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    // The term ends the day BEFORE the next period would start.
    const end = new Date(endYear, endMonth - 1, Math.min(d, lastDay));
    end.setDate(end.getDate() - 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
  }, [watchedPaymentFrequency, watchedNumberOfPayments, pptInceptionDate]);

  // ── Board #199 optional motor fields ──────────────────────────────────
  // Vehicle age is DERIVED, never keyed: whole years from the registration date
  // to today, the same arithmetic apps.commissions.services._motor_facts runs
  // when it matches an age band. `null` means "cannot be computed" and renders
  // as an empty box — a number nobody computed must never appear in one.
  const watchedRegistrationDate = watch('registration_date');
  const watchedInceptionDate = watch('inception_date');
  const vehicleAgeYears = useMemo(() => {
    if (!watchedRegistrationDate) return null;
    const registered = new Date(`${watchedRegistrationDate}T00:00:00`);
    if (Number.isNaN(registered.getTime())) return null;
    const today = new Date();
    const beforeAnniversary =
      today.getMonth() < registered.getMonth() ||
      (today.getMonth() === registered.getMonth() && today.getDate() < registered.getDate());
    const years = today.getFullYear() - registered.getFullYear() - (beforeAnniversary ? 1 : 0);
    return years >= 0 ? years : null;
  }, [watchedRegistrationDate]);

  // A vehicle registered on or after the policy starts is new business; anything
  // registered earlier is a rollover. ISO date strings compare correctly as
  // strings. This only ever fills the field — the broker can override it, and
  // once they do it stops moving under them.
  const derivedVehicleCondition = useMemo(() => {
    if (!watchedRegistrationDate || !watchedInceptionDate) return '';
    return watchedRegistrationDate >= watchedInceptionDate ? 'new' : 'old';
  }, [watchedRegistrationDate, watchedInceptionDate]);
  const vehicleConditionEdited = useRef(false);
  useEffect(() => {
    if (vehicleConditionEdited.current || !derivedVehicleCondition) return;
    setValue('vehicle_condition', derivedVehicleCondition);
  }, [derivedVehicleCondition, setValue]);

  // ── M-04: a NEW first registration has no plate and no registration date ──
  // Those two boxes are replaced by State / City / Pin Code / RTO.
  //
  // The switch is "condition is new AND there is no registration date", not
  // condition alone, and that second half matters: `derivedVehicleCondition`
  // answers "new" for any vehicle registered on or after the policy starts,
  // which includes a car registered LAST WEEK whose date the broker has just
  // typed. Hiding the field they are filling in — with their answer still
  // inside it, still submitted — is not what decision 13 asks for. Choosing
  // "New — first registration" by hand clears both boxes (see the Vehicle
  // Condition select below), which is what actually flips this.
  const watchedVehicleCondition = watch('vehicle_condition');
  const watchedVehicleNumber = watch('vehicle_number');
  const isNewRegistration =
    watchedVehicleCondition === 'new' && !watchedRegistrationDate && !watchedVehicleNumber;

  const watchedRtoId = watch('rto_id');
  const watchedRtoState = watch('rto_state');
  const watchedRtoCity = watch('rto_city');
  const watchedRtoPincode = watch('rto_pincode');

  // Edit mode: `policy_data` stores the RTO id alone, so the State and City the
  // block narrows by are read back off the office itself rather than stored a
  // second time. Also supplies the labels for the two selects while their own
  // option lists are still loading.
  const savedRto = useRTO(watchedRtoId || '');
  const rtoHydratedFor = useRef<string | null>(null);
  useEffect(() => {
    const detail = savedRto.data;
    if (!detail || rtoHydratedFor.current === detail.id) return;
    rtoHydratedFor.current = detail.id;
    if (detail.state && !getValues('rto_state')) setValue('rto_state', detail.state);
    if (detail.city && !getValues('rto_city')) setValue('rto_city', detail.city);
  }, [savedRto.data, setValue, getValues]);

  // M-03: Products are scoped to the chosen insurer AND the whole
  // classification -- insurance type, policy type, group, subgroup. `match:
  // 'wildcard'` is this form's opt-in: insurance type and group are strict,
  // but a catalog product that leaves subgroup / policy type unset is offered
  // under any subgroup / policy type (most production products carry a group
  // and nothing below it, and a strict filter hid them). The quote wizard
  // keeps strict matching by not sending it.
  const selectedPolicyType = watch('policy_type');
  const selectedSubgroup = watch('subgroup');
  const productsLookupEnabled = !!(selectedInsuranceType || selectedInsurer);
  const { data: products, isLoading: productsLoading } = useProductsLookup({
    insurance_type: selectedInsuranceType || undefined,
    insurer: selectedInsurer || undefined,
    policy_type: selectedPolicyType || undefined,
    group: selectedGroup || undefined,
    subgroup: selectedSubgroup || undefined,
    match: 'wildcard',
  });

  // M-03: a product picked under one combination must not silently survive a
  // change to it -- it would be saved against a classification it does not
  // belong to. Only a USER change arms this (the dimension onChange handlers
  // call markProductDimensionTouched): edit mode's reset() and the lead/quote
  // prefills write every dimension at once, and a saved policy whose product
  // the catalog has since reclassified must still open with that product
  // selected, not blanked. `products` is undefined while the new
  // combination's list is in flight, so the check only ever runs against the
  // list for the CURRENT combination.
  const productDimensionTouchedRef = useRef(false);
  const markProductDimensionTouched = () => {
    productDimensionTouchedRef.current = true;
  };
  useEffect(() => {
    if (!productDimensionTouchedRef.current || !selectedProduct) return;
    if (productsLookupEnabled && !products) return;
    if (productsLookupEnabled && products?.some((p) => p.id === selectedProduct)) return;
    setValue('product', '', { shouldValidate: true });
  }, [products, productsLookupEnabled, selectedProduct, setValue]);

  const { data: makes, isLoading: makesLoading } = useVehicleMakes();
  const { data: models, isLoading: modelsLoading } = useVehicleModels(selectedMake || undefined);
  const { data: variants, isLoading: variantsLoading } = useVehicleVariants(selectedModel || undefined);
  // B1: the same Country-Admin master the Vehicle Variants admin page writes
  // against — `lookup` returns ACTIVE fuel types only.
  const { data: fuelTypes, isLoading: fuelTypesLoading } = useFuelTypesLookup();

  useEffect(() => {
    if (!selectedCustomer) return;
    setValue(
      'holder_name',
      selectedCustomer.full_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim()
    );
    setValue('holder_phone', selectedCustomer.phone || '');
    setValue('holder_email', selectedCustomer.email || '');
    // Task 8: the contact's TYPE has to come across as well. Copying only
    // name/phone/email left every linked contact looking like an "individual"
    // holder, which is why linking Corporate contact still asked the company
    // for a date of birth. Blank falls back to the form's own default.
    const customerType = selectedCustomer.customer_type || 'individual';
    setValue('holder_customer_type', customerType);
    // ...and so do the three the broker was still re-keying by hand off a
    // contact record that already held them. Pincode was in the list payload
    // all along and simply was not read; date of birth and gender were added
    // to CustomerListSerializer for this.
    setValue('holder_pincode', selectedCustomer.pincode || '');
    // A company has no birthday and no gender, and the form hides both fields
    // for those types (isNaturalPersonType). Writing them anyway would leave a
    // value sitting in a hidden field to be submitted -- so the entity branch
    // clears them rather than carrying a natural person's data across a type
    // change.
    if (isNaturalPersonType(customerType)) {
      setValue('holder_dob', selectedCustomer.date_of_birth || '');
      setValue('holder_gender', selectedCustomer.gender || '');
    } else {
      setValue('holder_dob', '');
      setValue('holder_gender', '');
    }
  }, [selectedCustomer, setValue]);

  // Wave E: when the new-holder's Customer Type changes, clear a salutation
  // that no longer belongs to that type's option set (mirrors
  // CustomerFormPage's same-purpose effect) and clear Last Name when the
  // type switches to a company-name type (Corporate/Proprietorship), which
  // hides that input.
  useEffect(() => {
    const allowedSalutations = new Set(salutationCodesFor(holderCustomerType));
    const currentSalutation = getValues('holder_salutation');
    if (currentSalutation && !allowedSalutations.has(currentSalutation)) {
      setValue('holder_salutation', '');
    }
    if (isCompanyNameType(holderCustomerType) && getValues('holder_last_name')) {
      setValue('holder_last_name', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holderCustomerType]);

  // Item 7: net_premium / total_premium are auto-derived and read-only —
  // the user only ever edits the four components below.
  //
  // CRITICAL: only re-derive once the user has actually EDITED one of the four
  // components. On an edit-mode `reset()` load (and for legacy/back-entered or
  // health/life policies whose od/tp are 0 but whose net/total were entered
  // directly), the components are 0 and NOT dirty -- deriving then would zero
  // out the persisted net/total and, on save, overwrite the real premiums with
  // 0 (financial-data corruption; round-3 final-review Critical). reset() clears
  // dirty, so on load nothing is dirty and we leave the persisted net/total
  // untouched; typing into a component marks it dirty and re-arms the derivation.
  // setValue() here does not pass shouldDirty, so writing net/total never marks
  // the components dirty. Blank/unparsable component values count as 0.
  //
  // Item 11 adds `isMotorPolicy` to the gate. The four components are only
  // RENDERED for motor now, so on any other type they cannot become dirty and
  // this could not fire anyway — but a broker who fills OD/TP and then changes
  // the insurance type to Health would otherwise leave this effect armed,
  // fighting the non-motor derivation below over the same two fields. Same
  // data-driven flag as everywhere else in this form; no second motor rule.
  useEffect(() => {
    if (!isMotorPolicy) return;
    const userEditedComponent =
      !!dirtyFields.od_premium ||
      !!dirtyFields.od_tax ||
      !!dirtyFields.tp_premium ||
      !!dirtyFields.tp_tax;
    if (!userEditedComponent) return;
    const parseAmount = (v: string | undefined) => {
      const n = parseFloat(v ?? '');
      return Number.isFinite(n) ? n : 0;
    };
    const net = parseAmount(odPremium) + parseAmount(tpPremium);
    const total = net + parseAmount(odTax) + parseAmount(tpTax);
    setValue('net_premium', net.toFixed(2));
    setValue('total_premium', total.toFixed(2));
  }, [odPremium, odTax, tpPremium, tpTax, setValue, dirtyFields, isMotorPolicy]);

  // Item 11 — the NON-motor half. Health, Life, Term, Travel, Fire and the
  // rest have no OD/TP split: the broker types Net Premium and Taxes, and
  // Total Premium is the two added up.
  //
  // THE DIRTY GATE IS NOT OPTIONAL, for exactly the reason spelled out above
  // the motor effect. A health or life policy stores net_premium and
  // total_premium that were entered directly; deriving unconditionally would
  // fire on the edit-mode reset(), when `taxes` is whatever was stored and
  // net is whatever was stored, and — worse — on a policy whose total carries
  // stamp duty or a loading, would silently rewrite the persisted total to
  // net + tax on an edit that never touched the premium at all. reset()
  // clears every dirty flag, so opening a policy derives nothing; typing into
  // Net Premium or Taxes arms it.
  useEffect(() => {
    if (isMotorPolicy) return;
    const userEditedComponent = !!dirtyFields.net_premium || !!dirtyFields.gst_amount;
    if (!userEditedComponent) return;
    const parseAmount = (v: string | undefined) => {
      const n = parseFloat(v ?? '');
      return Number.isFinite(n) ? n : 0;
    };
    const total = parseAmount(netPremium) + parseAmount(taxes);
    setValue('total_premium', total.toFixed(2));
  }, [netPremium, taxes, setValue, dirtyFields, isMotorPolicy]);

  // The premium block has two mutually exclusive halves — motor's OD/TP split
  // plus IDV, and everything else's Taxes plus Sum Assured — and only one is
  // ever mounted. react-hook-form KEEPS a field's value when its input
  // unmounts (`shouldUnregister` defaults to false; the Task 8 note further
  // down this file relies on exactly that), so typing 5,400 into Taxes on a
  // health schema and then switching the Insurance Type to Motor left
  // `gst_amount: '5400'` in the form with no input on screen able to show or
  // correct it — and the submit loop posted it, putting a phantom tax on a
  // motor policy and into every premium report downstream. Changing the
  // Insurance Type only reset policy_type/group/subgroup.
  //
  // So drop the half that just stopped applying. Two rules make this safe:
  //
  //  * Only on a REAL transition. `schema.data` is undefined until a type is
  //    chosen (useInsuranceTypeSchema is `enabled: !!insuranceType`) and again
  //    while the new type's schema loads, so the first resolved value only
  //    RECORDS the starting side. Firing on the initial resolution instead
  //    would zero the stored gst_amount of any motor policy the moment its
  //    edit page loaded — the same load-time clobber the two derivation
  //    effects above are gated against.
  //  * Values only, no `shouldDirty`. Marking these dirty would arm the
  //    derivation effect for the half we just emptied and rewrite a stored
  //    net/total on a mere type correction.
  //
  // Total Premium deliberately keeps its pre-switch value: it is the figure
  // the broker actually recorded, and re-deriving it from a half-migrated
  // block would zero a real premium on what may be a one-click mis-click.
  // Entering the new half's components re-arms the matching derivation.
  const previousSchemaIsMotor = useRef<boolean | null>(null);
  useEffect(() => {
    if (!schema.data) return;
    const previous = previousSchemaIsMotor.current;
    previousSchemaIsMotor.current = isMotorPolicy;
    if (previous === null || previous === isMotorPolicy) return;
    if (isMotorPolicy) {
      setValue('gst_amount', '');
      setValue('sum_insured', '');
    } else {
      setValue('od_premium', '');
      setValue('od_tax', '');
      setValue('tp_premium', '');
      setValue('tp_tax', '');
      setValue('idv', '');
    }
  }, [isMotorPolicy, schema.data, setValue]);

  // B1 (15 Sep QA batch): the same unmount-keeps-value trap as the OD/TP/Taxes
  // block above, for Premium Paying Term. `payment_frequency` and
  // `number_of_payments` are plain `register()` inputs, so switching the
  // Insurance Type away from an allowed one (Health/Life/Term/Investment)
  // hides the section but leaves react-hook-form still holding whatever the
  // broker had typed -- which the submit loop would still post, and the
  // server would now refuse (`installments_refused_reason`) with a field
  // error pointing at an input the broker can no longer see. Clear both, but
  // only on a REAL transition (same guard as above): wait for the lookup to
  // resolve so the first render just records the starting side instead of
  // wiping a genuine installment policy's fields the moment its edit page
  // loads.
  const previousSupportsInstallments = useRef<boolean | null>(null);
  useEffect(() => {
    if (!insuranceTypesLookup.data) return;
    const previous = previousSupportsInstallments.current;
    previousSupportsInstallments.current = supportsInstallments;
    if (previous === null || previous === supportsInstallments) return;
    if (!supportsInstallments) {
      setValue('payment_frequency', '');
      setValue('number_of_payments', '');
    }
  }, [supportsInstallments, insuranceTypesLookup.data, setValue]);

  // B-01 (24 Sep): prefill Installment Amount from the Total Premium. On a
  // Health/Life/Term policy the premium a broker types IS the per-installment
  // premium, so that is the right starting figure -- but it stays editable.
  // The box FOLLOWS the total only while it is blank or still holds the
  // total it was last filled with; once the broker types a different figure
  // (or the edit page loads a saved one that differs), that figure is theirs
  // and a later premium change must not overwrite it.
  const watchedTotalPremium = watch('total_premium');
  const lastPrefilledInstallment = useRef('');
  // Set by the edit-mode reset() below for a policy that is ALREADY on a term
  // with a stored null amount. Its schedule was built as total / N, the old
  // convention, so offering the total here would PATCH installment_amount =
  // total on an unrelated edit (a nominee, a typo) and the next rebuild would
  // schedule N x total. The box stays blank (blank sends null, keeping the
  // stored null) until the broker types a figure themselves.
  const installmentPrefillHeld = useRef(false);
  useEffect(() => {
    const total = watchedTotalPremium ?? '';
    const previous = lastPrefilledInstallment.current;
    lastPrefilledInstallment.current = total;
    if (installmentPrefillHeld.current) return;
    const current = getValues('installment_amount') ?? '';
    if (current === '' || current === previous) {
      setValue('installment_amount', total);
    }
  }, [watchedTotalPremium, getValues, setValue]);

  // BROKER-2b: auto-derive the Policy Term (months) from the coverage dates,
  // mirroring the premium-derivation effect above. Ceil semantics — a policy
  // running 2025-01-01 -> 2025-12-31 counts as 12 months: the final partial
  // month rounds up once the expiry day-of-month passes the inception day.
  // Dates are parsed from the YYYY-MM-DD input strings directly (not `new
  // Date()`) so a browser timezone behind UTC can't shift the day.
  //
  // Task 14: this was gated on `!dirtyFields.policy_term_months` — true right
  // after ANY reset(), including the edit-mode load below, since reset()
  // clears every dirty flag. That let this effect silently recompute and
  // overwrite the just-loaded, truly-saved term the instant the edit page
  // (re)mounted, before the user touched anything — e.g. a policy sold with
  // a genuine 3-month short-term cover displayed (and would have re-saved)
  // as 12 the moment its edit page was refreshed, because 3 != the ceil-
  // months the dates alone imply. Verified against a policy_dev clone of
  // production: 21 of 542 real policies have a persisted policy_term_months
  // the date math would not reproduce.
  //
  // Gate on the DATE fields actually being user-edited instead (the same
  // shape as the premium effect's `userEditedComponent` gate above): a
  // reset() leaves inception_date/expiry_date not-dirty too, so loading a
  // policy never fires this; typing into either date field does, so
  // "changing the dates re-derives" (BROKER-2b's original intent) still
  // holds during interactive use, create or edit.
  useEffect(() => {
    const userEditedDates = !!dirtyFields.inception_date || !!dirtyFields.expiry_date;
    if (!userEditedDates) return;
    const parse = (s: string | undefined) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
      return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
    };
    const start = parse(inceptionDate);
    const end = parse(expiryDate);
    if (!start || !end) return;
    const startKey = start.y * 10000 + start.mo * 100 + start.d;
    const endKey = end.y * 10000 + end.mo * 100 + end.d;
    if (endKey <= startKey) return;
    let months = (end.y - start.y) * 12 + (end.mo - start.mo);
    if (end.d > start.d) months += 1;
    if (months < 1) months = 1;
    setValue('policy_term_months', String(months));
  }, [inceptionDate, expiryDate, setValue, dirtyFields]);

  // Edit mode: prefill the form from the existing policy.
  useEffect(() => {
    if (!isEdit || !existingPolicy) return;
    const p = existingPolicy as unknown as Record<string, unknown>;
    const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
    reset({
      insurance_type: str(p.insurance_type),
      policy_type: str(p.policy_type),
      group: str(p.group),
      subgroup: str(p.subgroup),
      insurer: str(p.insurer),
      previous_insurer: str(p.previous_insurer),
      product: str(p.product),
      customer: str(p.customer),
      holder_name: str(p.holder_name),
      holder_phone: str(p.holder_phone),
      holder_email: str(p.holder_email),
      holder_dob: str(p.holder_dob),
      holder_gender: str(p.holder_gender),
      holder_pincode: str(p.holder_pincode),
      // Task 8: reopening a corporate policy must not ask the company for a
      // date of birth all over again. Blank on every policy written before the
      // column existed, which reads as "individual" (isNaturalPersonType).
      holder_customer_type: str(p.holder_customer_type),
      inception_date: str(p.inception_date),
      expiry_date: str(p.expiry_date),
      issue_date: str(p.issue_date),
      grace_period: str(p.grace_period),
      policy_term_months: str(p.policy_term_months),
      // reset() replaces EVERY value, so a key left out here comes back blank
      // and onSubmit writes that blank back -- which would silently take a
      // life policy off its premium-paying term on an unrelated edit.
      payment_frequency: str(p.payment_frequency),
      number_of_payments: str(p.number_of_payments),
      // B-01: the SAVED per-installment amount. A stored null comes back
      // blank; on a policy already on a term it STAYS blank (see
      // installmentPrefillHeld), otherwise the prefill offers the total.
      installment_amount: str(p.installment_amount),
      od_premium: str(p.od_premium),
      od_tax: str(p.od_tax),
      tp_premium: str(p.tp_premium),
      tp_tax: str(p.tp_tax),
      net_premium: str(p.net_premium),
      total_premium: str(p.total_premium),
      // Left out of reset() this key would come back BLANK and onSubmit would
      // write that blank over the stored tax — the same trap the comment above
      // describes for payment_frequency.
      gst_amount: str(p.gst_amount),
      vehicle_number: str(p.vehicle_number),
      registration_date: str(p.registration_date),
      manufacturing_year: str(p.manufacturing_year),
      ncb_percentage: str(p.ncb_percentage),
      vehicle_make: str(p.vehicle_make),
      vehicle_model: str(p.vehicle_model),
      vehicle_variant: str(p.vehicle_variant),
      fuel_type: str(p.fuel_type),
      idv: str(p.idv),
      sum_insured: str(p.sum_insured),
      posp: str(p.posp),
      insurer_policy_number: str(p.insurer_policy_number),
      // These three have no Policy column and live only in `policy_data`.
      // reset() replaces EVERY value, so a key left out of it comes back blank —
      // and the schema fold in onSubmit then writes that blank back. Editing a
      // motor policy's phone number was silently erasing its Engine CC and GVW.
      cc: policyDataValue(p, 'cc'),
      gvw: policyDataValue(p, 'gvw'),
      // B-04: same bag, same reset() trap. A policy converted from a quote
      // carries them on its PolicyVehicle instead, so fall back to that.
      engine_number: policyDataValue(p, 'engine_number') || vehicleDetailValue(p, 'engine_number'),
      chassis_number: policyDataValue(p, 'chassis_number') || vehicleDetailValue(p, 'chassis_number'),
      // #199: editing a policy that already carries a condition must not
      // silently re-derive a different answer — hence the touched flag below.
      vehicle_condition: savedVehicleCondition(p),
      motor_optional: savedMotorOptional(p),
      // #332: a non-motor policy's nominee is editable now (the update
      // serializer accepts `nominees`). Motor renders no nominee box on edit,
      // so prefilling it there is harmless.
      nominee_name: savedNominee(p, 'name'),
      nominee_relationship: savedNominee(p, 'relationship'),
      nominee_share: savedNominee(p, 'percentage_share'),
      master_policy_number: str(p.master_policy_number),
      master_policy_name: str(p.master_policy_name),
      // M-04: the RTO and the PIN it was found by live only in `policy_data`
      // too, and are subject to exactly the same reset() erasure as cc/gvw
      // above. State and City are NOT reset here: they are rehydrated from the
      // RTO itself (see the useRTO effect), so there is nothing to lose.
      rto_id: policyDataValue(p, 'rto_id'),
      rto_pincode: policyDataValue(p, 'rto_pincode'),
      rto_state: '',
      rto_city: '',
      notes: str(p.notes),
    });
    if (savedVehicleCondition(p)) vehicleConditionEdited.current = true;
    installmentPrefillHeld.current =
      !!p.payment_frequency && (p.installment_amount === null || p.installment_amount === undefined || p.installment_amount === '');
  }, [isEdit, existingPolicy, reset]);

  // #332: insured members. A fresh form starts with the rows the structure
  // implies (individual: one fixed "self"; floater: self + one). Changing the
  // group keeps whatever the broker already typed and the block warns instead
  // of discarding; only untouched placeholder rows are rebuilt for the new mode.
  const memberMode = memberStructure?.mode;
  useEffect(() => {
    if (!memberStructure) return;
    if (isEdit && !existingPolicy) return;
    const key = editId ?? 'new';
    const saved = isEdit
      ? membersFromPolicy((existingPolicy as unknown as Record<string, unknown>)?.members)
      : [];
    if (saved.length > 0 && membersLoadedFor.current !== key) {
      membersLoadedFor.current = key;
      setMembers(saved);
      return;
    }
    setMembers((prev) => {
      const untouched = prev.every(
        (r) => !r.name && !r.dob && !r.gender && !r.ped && !r.height_cm && !r.weight_kg
      );
      return untouched ? initialMembers(memberStructure) : prev;
    });
    // memberStructure is derived from memberMode + the server response; the
    // mode (and the policy being edited) are what should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberMode, isEdit, existingPolicy, editId]);

  // #332: registry-driven values live in policy_data. reset() above cannot know
  // which keys the registry will offer (the schema loads separately), so restore
  // each rendered key once, without marking the form dirty.
  // Restored for every load of the policy being edited (reset() runs again
  // whenever `existingPolicy` changes and blanks these), but only once per
  // load so a value the broker clears afterwards is not put back.
  const hydratedRegistry = useRef<{ policy: unknown; keys: Set<string> }>({
    policy: null,
    keys: new Set(),
  });
  useEffect(() => {
    if (!isEdit || !existingPolicy || !memberStructure) return;
    if (hydratedRegistry.current.policy !== existingPolicy) {
      hydratedRegistry.current = { policy: existingPolicy, keys: new Set() };
    }
    const done = hydratedRegistry.current.keys;
    const bag = ((existingPolicy as unknown as Record<string, unknown>).policy_data ?? {}) as Record<
      string,
      unknown
    >;
    for (const f of registryFields) {
      if (done.has(f.key)) continue;
      done.add(f.key);
      const current = (getValues as (k: string) => unknown)(f.key);
      const stored = bag[f.key];
      if ((current === undefined || current === '') && stored !== undefined && stored !== null) {
        (setValue as (k: string, v: unknown) => void)(f.key, stored);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingPolicy, memberStructure, schemaFields.length]);

  // B3: prefill from the quote being converted.
  //
  // "Convert to Policy" on a completed quote navigates to
  // /policies/new?quote=<id>. The parameter was read by nothing, so the broker
  // re-keyed every customer, quote and vehicle detail they had just entered in
  // the wizard.
  //
  // Runs once, create mode only, and never overwrites a field the user has
  // already filled -- same contract as the last-context prefill below. Failure
  // is silent: a prefill that cannot load must still leave a usable blank form.
  useEffect(() => {
    if (isEdit || !preselectedQuote) return;
    let cancelled = false;

    quoteService
      .getQuote(preselectedQuote)
      .then((quote) => {
        if (cancelled) return;
        const q = quote as unknown as Record<string, unknown>;
        const str = (key: string) => {
          const value = q[key];
          return value === null || value === undefined ? '' : String(value);
        };

        const patch: Partial<Record<keyof PolicyCreateFormData, string>> = {
          // Customer + classification
          customer: str('customer_profile_id'),
          insurance_type: str('insurance_type'),
          group: str('insurance_group'),
          subgroup: str('insurance_subgroup'),
          // Holder identity, for a quote whose customer has no Contact row yet
          holder_name: str('proposer_name'),
          holder_email: str('proposer_email'),
          holder_phone: str('proposer_phone'),
          holder_dob: str('proposer_dob'),
          holder_gender: str('proposer_gender'),
          holder_pincode: str('proposer_pincode'),
          holder_customer_type: str('proposer_type'),
          // Vehicle
          vehicle_number: str('registration_number'),
          registration_date: str('registration_date'),
          manufacturing_year: str('manufacturing_year'),
          vehicle_variant: str('vehicle_variant'),
          ncb_percentage: str('previous_ncb'),
          // Agent carried over from the quote
          posp: str('posp'),
        };

        for (const [field, value] of Object.entries(patch)) {
          if (!value) continue;
          const current = getValues(field as keyof PolicyCreateFormData);
          if (!current) {
            setValue(field as keyof PolicyCreateFormData, value);
          }
        }
      })
      .catch(() => {
        // silent — prefill is best-effort, the form still opens usable
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, preselectedQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill from the lead being converted (`?lead=<id>`).
  //
  // Make / Model / Variant arrive as master IDS where the lead's free text
  // resolved and as '' where it did not -- the server will not guess a
  // variant, because a wrong one reprices the policy. The unresolved text is
  // returned too and shown as a hint, so the broker can see what the lead
  // actually said while they pick the right one.
  //
  // Same contract as the quote prefill: create mode only, never overwrites a
  // field already filled, and fails silently so the form still opens usable.
  useEffect(() => {
    if (isEdit || !preselectedLead) return;
    let cancelled = false;

    leadService
      .getPolicyPrefill(preselectedLead)
      .then((prefill) => {
        if (cancelled) return;
        setPrefillVehicleText({
          source: 'Lead',
          make: prefill.vehicle?.vehicle_make_text || '',
          model: prefill.vehicle?.vehicle_model_text || '',
          variant: prefill.vehicle?.vehicle_variant_text || '',
          fuel: prefill.vehicle?.fuel_type_text || '',
        });

        // B11 second half. The server has always sent `cover` -- the insurance
        // type and company the lead recorded, as this form's own ids -- and
        // nothing read it, so a broker opening Add Policy from a lead re-picked
        // both by hand from the lead sitting open in another tab. An id that
        // did not resolve arrives as '' with its label beside it and is shown
        // as a hint rather than guessed at.
        setPrefillCoverText({
          source: 'Lead',
          insuranceType: prefill.cover?.insurance_type ? '' : prefill.cover?.insurance_type_label || '',
        });

        const patch: Record<string, string> = {
          customer: prefill.customer || '',
          ...(prefill.holder ?? {}),
          insurance_type: prefill.cover?.insurance_type || '',
          insurer: prefill.cover?.insurer || '',
          product: prefill.cover?.product || '',
          vehicle_number: prefill.vehicle?.vehicle_number || '',
          manufacturing_year: prefill.vehicle?.manufacturing_year || '',
          vehicle_make: prefill.vehicle?.vehicle_make || '',
          vehicle_model: prefill.vehicle?.vehicle_model || '',
          vehicle_variant: prefill.vehicle?.vehicle_variant || '',
          vehicle_condition: prefill.vehicle?.vehicle_condition || '',
        };

        for (const [field, value] of Object.entries(patch)) {
          if (!value) continue;
          const current = getValues(field as keyof PolicyCreateFormData);
          if (!current) {
            setValue(field as keyof PolicyCreateFormData, value);
          }
        }
      })
      .catch(() => {
        // silent — prefill is best-effort, the form still opens usable
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, preselectedLead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Write ONE of the contact's vehicles onto the form.
  //
  // `overwrite` is the difference between the two ways this is reached. An
  // automatic prefill (the contact has exactly one vehicle) fills only what is
  // still blank, so it can never clobber something the broker typed first.
  // An explicit pick from the selector below overwrites -- INCLUDING clearing
  // fields the newly chosen vehicle does not have. Switching from the Swift to
  // the i20 and leaving the Swift's variant behind would compose a vehicle
  // that does not exist and price it.
  const applyContactVehicle = (vehicle: ContactPrefillVehicle, overwrite: boolean) => {
    setPrefillVehicleText({
      source: 'Contact',
      make: vehicle.vehicle_make_text || '',
      model: vehicle.vehicle_model_text || '',
      variant: vehicle.vehicle_variant_text || '',
      fuel: vehicle.fuel_type_text || '',
    });
    const patch: Record<string, string> = {
      vehicle_number: vehicle.vehicle_number || '',
      registration_date: vehicle.registration_date || '',
      manufacturing_year: vehicle.manufacturing_year || '',
      vehicle_make: vehicle.vehicle_make || '',
      vehicle_model: vehicle.vehicle_model || '',
      vehicle_variant: vehicle.vehicle_variant || '',
      ncb_percentage: vehicle.ncb_percentage || '',
      cc: vehicle.cc || '',
      gvw: vehicle.gvw || '',
      // B-04: including what a vendor RC lookup bought for this number.
      engine_number: vehicle.engine_number || '',
      chassis_number: vehicle.chassis_number || '',
    };
    for (const [field, value] of Object.entries(patch)) {
      const key = field as keyof PolicyCreateFormData;
      if (!value) {
        if (overwrite) setValue(key, '');
        continue;
      }
      if (overwrite || !getValues(key)) setValue(key, value);
    }
  };

  // Did what is on screen come from the shared registration trail rather than
  // this broker's own record for the contact? Derived, not stored, so it
  // follows the picker.
  const chosenVehicleFromTrail =
    contactVehicles.find((v) => v.id === chosenContactVehicleId)?.source === 'registration_trail';

  // Item 2 -- prefill from the contact this policy is being written for
  // (`?customer=<id>`, set by "Create Policy" on the contact page and by the
  // contact quick-actions menu).
  //
  // Until now that parameter did exactly one thing: seed the customer select.
  // The contact's vehicle -- registration number, make, model, variant, year,
  // all of it already on the contact record -- was re-typed by hand every
  // time. The server half is apps/customers/prefill.py; it resolves the
  // contact's free text onto the vehicle master and returns '' for anything it
  // cannot resolve, because a guessed variant reprices the policy.
  //
  // ONE vehicle prefills straight away. SEVERAL are held for the picker below
  // and nothing is written until the broker chooses.
  //
  // Create mode only, and it never overwrites a field the user has already
  // filled -- the same contract as the lead and quote prefills above.
  useEffect(() => {
    if (isEdit || !preselectedCustomer) return;
    let cancelled = false;
    setContactPrefillFailed(false);

    getContactPolicyPrefill(preselectedCustomer)
      .then((prefill) => {
        if (cancelled) return;
        for (const [field, value] of Object.entries(prefill.holder ?? {})) {
          if (!value) continue;
          const key = field as keyof PolicyCreateFormData;
          if (!getValues(key)) setValue(key, value);
        }
        const vehicles = prefill.vehicles ?? [];
        setContactVehicles(vehicles);
        if (vehicles.length === 1) {
          setChosenContactVehicleId(vehicles[0].id);
          applyContactVehicle(vehicles[0], false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Surfaced in the Vehicle Details card, not swallowed. The form stays
        // usable -- every field can still be typed -- but the broker is told
        // that what they are looking at is a blank form, not this contact's
        // record.
        setContactPrefillFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, preselectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create mode only: auto-fill Policy Context from the user's last policy entry.
  // Runs once on mount; skips any field the user has already touched.
  // Never sets insurer_policy_number (Policy Number stays blank).
  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    policyService.getLastContext().then((ctx) => {
      if (cancelled) return;
      const patch = applyLastContext(ctx as LastContext);
      for (const [field, value] of Object.entries(patch)) {
        const current = getValues(field as keyof PolicyCreateFormData);
        if (!current) {
          setValue(field as keyof PolicyCreateFormData, value);
        }
      }
    }).catch(() => {
      // silent — prefill is best-effort
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const insurerOptions = (brokerInsurers || []).map((i) => ({ value: i.id, label: i.name }));
  // #588: the previous insurer may be any company, not only the broker's connected ones.
  const { data: insurerLookup } = useInsurersLookup();
  const previousInsurerOptions = (insurerLookup ?? []).map((i) => ({ value: i.id, label: i.name }));
  // T13a: label from the insurer's own mapping name, not the catalog
  // product's creation-time name — the two can differ, and the mapping name
  // is what the country admin actually assigned for this insurer.
  const lookupProductOptions = (products || []).map((p) => ({ value: p.id, label: p.mapped_name ?? p.name }));
  // B4 — the three vehicle selects cascade: models only load once a make has
  // resolved, variants once a model has. Edit mode's reset() writes all three
  // saved ids at once, long before those lookups return, so each <select> held
  // a value that was not in its own option list and rendered blank. After a
  // hard refresh the lookups came from the TanStack cache and resolved with the
  // reset, which is exactly why the values "were correct after a refresh".
  //
  // The fix keeps the saved value self-sustaining instead of trying to order
  // the fetches: when the freshly-loaded list does not contain the id the form
  // holds, prepend a synthetic option carrying that id and the name the policy
  // payload already returned. Same technique as PoliciesListPage's insurer
  // filter. The selects are bound to the form value (rather than left
  // uncontrolled) so React re-applies it the moment the matching option
  // exists — an uncontrolled <select> never revisits its value when its
  // options change.
  const savedPolicyName = (key: string): string | undefined => {
    const value = (existingPolicy as unknown as Record<string, unknown> | undefined)?.[key];
    return typeof value === 'string' && value ? value : undefined;
  };
  const withSavedOption = (
    options: { value: string; label: string }[],
    selectedId: string | undefined,
    savedLabel: string | undefined
  ) => {
    if (!selectedId || options.some((option) => option.value === selectedId)) return options;
    return [{ value: selectedId, label: savedLabel || 'Saved selection' }, ...options];
  };

  // M-03: in edit mode the saved product is always an option, labelled from
  // the policy payload, even when the catalog has since moved it out of this
  // combination -- otherwise the select renders its placeholder and the form
  // looks like the product was cleared. Only the SAVED id is pinned: a product
  // the user picks always comes from the fetched list.
  const savedProductId = isEdit ? String((existingPolicy as { product?: unknown } | undefined)?.product ?? '') : '';
  const productOptions = withSavedOption(
    lookupProductOptions,
    selectedProduct && selectedProduct === savedProductId ? selectedProduct : undefined,
    savedPolicyName('product_name')
  );
  const noProductsForCombination =
    productsLookupEnabled && !productsLoading && !!products && productOptions.length === 0;

  // Task 3 (board): `posps` is now a SEARCH result, not the whole directory, so
  // the currently-selected agent is routinely absent from it. SearchableSelect
  // resolves its displayed label with `options.find(o => o.value === value)`,
  // so a value with no matching option renders the placeholder — on the edit
  // form that looks exactly like the agent was cleared, and the next save
  // really would clear it. Keep the selection in the list either way: pinned
  // from the option the user picked, or — in edit mode, where the saved agent
  // may never have been in any fetched page — from the `posp_name` the policy
  // payload already carries. Same technique as Make/Model/Variant above.
  const pospOptions = withSavedOption(
    [
      { value: '', label: 'Select Agent' },
      ...(posps || []).map((p) => ({ value: p.id, label: pospOptionLabel(p) })),
    ],
    selectedPospId || undefined,
    (pinnedPosp && pinnedPosp.value === selectedPospId ? pinnedPosp.label : undefined) ||
      savedPolicyName('posp_name')
  );

  const makeOptions = useMemo(
    () =>
      withSavedOption(
        (makes || []).map((m) => ({ value: m.id, label: m.name })),
        selectedMake,
        savedPolicyName('vehicle_make_name')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makes, selectedMake, existingPolicy]
  );
  const modelOptions = useMemo(
    () =>
      withSavedOption(
        (models || []).map((m) => ({ value: m.id, label: m.name })),
        selectedModel,
        savedPolicyName('vehicle_model_name')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [models, selectedModel, existingPolicy]
  );
  const variantOptions = useMemo(
    () =>
      withSavedOption(
        (variants || []).map((v) => ({ value: v.id, label: v.name })),
        selectedVariant,
        savedPolicyName('vehicle_variant_name')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variants, selectedVariant, existingPolicy]
  );
  const fuelTypeOptions = (fuelTypes || []).map((f) => ({ value: f.id, label: f.name }));

  const onSubmit = async (data: PolicyCreateFormData) => {
    // Rapid double-click guard: the first click sets this synchronously
    // (before any await), so a second click arriving before the button's
    // disabled state re-renders is dropped here instead of firing a second
    // request.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setServerErrors([]);
    try {
      // Cover must end after it starts. The server refuses this too (B9 lane
      // A); checking here puts the message under Expiry Date before a round
      // trip. Six live policies had expiry before start until 15 Sep.
      if (data.inception_date && data.expiry_date && data.expiry_date <= data.inception_date) {
        setError('expiry_date', { type: 'manual', message: 'Expiry date must be after the start date' });
        return;
      }
      // B12 -- an unusually long term is confirmed, not blocked: the regex in
      // the schema above puts no ceiling on `policy_term_months` at all, and
      // a real ten-year policy exists in prod (2026-08-06 -> 2036-08-05).
      // Asked once per distinct value (`ackLongTermMonthsRef`) so re-submitting
      // after "Yes" (or after fixing some OTHER field on the same long term)
      // does not ask again for the identical number.
      const termMonths = data.policy_term_months ? parseInt(data.policy_term_months, 10) : NaN;
      if (
        data.policy_term_months &&
        Number.isFinite(termMonths) &&
        termMonths > LONG_POLICY_TERM_MONTHS &&
        ackLongTermMonthsRef.current !== data.policy_term_months
      ) {
        setLongTermConfirmMonths(data.policy_term_months);
        return;
      }
      // Conditional-mandatory classification fields (FIX 11): each field is
      // required only when its dropdown actually offers options.
      let classificationInvalid = false;
      if (classification.policyType.state.required && !data.policy_type) {
        setError('policy_type', { message: 'Select a policy type' });
        classificationInvalid = true;
      }
      if (classification.group.state.required && !data.group) {
        setError('group', { message: 'Select a group' });
        classificationInvalid = true;
      }
      if (classification.subgroup.state.required && !data.subgroup) {
        setError('subgroup', { message: 'Select a subgroup' });
        classificationInvalid = true;
      }
      if (classificationInvalid) return;

      // Queue #218: Policy Number is mandatory when ADDING a policy. Not on
      // edit -- see the schema note above; a legacy policy with no number must
      // stay editable, or the field becomes a wall in front of every other
      // correction someone needs to make.
      if (!isEdit && !(data.insurer_policy_number || '').trim()) {
        setError('insurer_policy_number', { message: 'Policy number is required' });
        return;
      }

      // Holder validation + Policy.holder_name derivation. Wave E: for a
      // brand-new customer (useStructuredHolderFields), the form no longer
      // collects a single free-text Holder Name -- it's derived here from
      // the structured Customer Type/First/Last Name fields (join, not the
      // old naive split) and still sent as the Policy's own denormalized
      // holder_name. The customer-LINKED and edit-mode paths are untouched:
      // they still collect/validate holder_name directly.
      let holderName = data.holder_name;
      if (useStructuredHolderFields) {
        // Board #272: the holder IS the nested Contact, so its name is the
        // policy's holder name. Required-ness comes from the same schema the
        // inputs are rendered from, rather than a second hand-written list.
        holderName = newContactHolderName;
        const missing = basicFieldSpecs.find((spec) => spec.required && !contactValue(spec.key));
        if (missing) {
          toast.error(`Enter the ${missing.label.toLowerCase()}`);
          return;
        }
        if (!holderName) {
          toast.error('Enter the policy holder name');
          return;
        }
        if (!data.holder_phone) {
          toast.error('Enter the policy holder phone');
          return;
        }
      } else {
        if (!data.customer && !data.holder_name) {
          toast.error('Select an existing customer or enter holder name');
          return;
        }
        if (!data.customer && !data.holder_phone) {
          toast.error('Select an existing customer or enter holder phone');
          return;
        }
      }

      // Premium Paying Term: the pair must be complete. The server refuses a
      // half-set too, but saying so here means the broker sees which of the
      // two boxes is empty instead of a field error on a form they have
      // already scrolled past.
      if (data.payment_frequency && !data.number_of_payments) {
        setError('number_of_payments', { message: 'Enter how many payments the customer will make' });
        return;
      }
      if (data.number_of_payments && !data.payment_frequency) {
        setError('payment_frequency', { message: 'Choose a payment frequency' });
        return;
      }
      if (data.number_of_payments) {
        const count = parseInt(data.number_of_payments, 10);
        if (count < 1 || count > MAX_NUMBER_OF_PAYMENTS) {
          setError('number_of_payments', {
            message: `Number of payments must be between 1 and ${MAX_NUMBER_OF_PAYMENTS}`,
          });
          return;
        }
      }
      if (data.payment_frequency && data.installment_amount && !(parseFloat(data.installment_amount) > 0)) {
        setError('installment_amount', { message: 'An installment must be more than zero' });
        return;
      }

      // The same test as `isNewRegistration` (which drives the render), but
      // read off the SUBMITTED values rather than the watched ones, so what is
      // sent is decided by what is in the form at submit time.
      const submittedAsNewRegistration =
        data.vehicle_condition === 'new' && !data.registration_date && !data.vehicle_number;

      // #332: health member structure and registry-required fields. The server
      // re-checks all of it; this only saves a round trip.
      // Raw form values: the zod resolver strips keys it does not name, and the
      // registry-driven health fields are exactly those.
      const rawValues = getValues() as unknown as Record<string, unknown>;
      let submitMembers: MemberRow[] = members;
      const savedMemberCount = Array.isArray(
        (existingPolicy as unknown as Record<string, unknown> | undefined)?.members
      )
        ? (existingPolicy as unknown as { members: unknown[] }).members.length
        : 0;
      const masterTyped = !!(rawValues.master_policy_number || rawValues.master_policy_name);
      const legacyUntouched = isEdit && savedMemberCount === 0 && !membersTouched && !masterTyped;
      if (memberStructure && !legacyUntouched) {
        if (memberStructure.mode === 'group') {
          // Optional for group: a fully blank row is not a member.
          submitMembers = members.filter((r) => r.name || r.dob || r.gender || r.ped);
        }
        const memberProblems = validateMembers(submitMembers, memberStructure);
        const registryProblems: Record<string, string> = {};
        for (const f of registryFields) {
          const v = rawValues[f.key];
          if (f.is_required && (v === undefined || v === '' || v === null)) {
            registryProblems[f.key] = `${f.label} is required`;
          }
        }
        setMemberErrors(memberProblems);
        if (Object.keys(registryProblems).length) {
          Object.entries(registryProblems).forEach(([k, message]) =>
            (setError as (k: string, e: { message: string }) => void)(k, { message })
          );
        }
        if (Object.keys(memberProblems).length || Object.keys(registryProblems).length) {
          const problems = [...Object.values(memberProblems), ...Object.values(registryProblems)];
          setServerErrors(Array.from(new Set(problems)));
          toast.error(problems[0] ?? 'Check the highlighted fields');
          return;
        }
      }

      const payload: Record<string, unknown> = {};
      const numericKeys = new Set([
        'policy_term_months',
        'manufacturing_year',
        'ncb_percentage',
        'number_of_payments',
      ]);

      for (const [key, value] of Object.entries(data)) {
        if (value === '' || value === undefined) {
          // An EMPTIED numeric box has to say so. Edit is a PATCH, where an
          // absent key means "leave this column alone", so simply skipping the
          // value made every clear a silent no-op under a success toast —
          // the same trap `posp` and `payment_frequency` below were already
          // patched for, one field at a time. The worst of them was Taxes:
          // clearing it dropped `gst_amount` from the request while the
          // derivation effect still sent the recomputed `total_premium`,
          // storing net 20000 / gst 3600 / total 20000 on a policy whose tax
          // no longer had any relationship to its own total.
          if (isEdit && CLEARED_AMOUNT_ON_EDIT.has(key)) {
            payload[key] = CLEARED_AMOUNT_ON_EDIT.get(key);
          }
          continue;
        }
        if (numericKeys.has(key)) {
          payload[key] = parseInt(value as string, 10);
        } else {
          payload[key] = value;
        }
      }
      // #251 — the loop above drops every empty value, which is right for
      // create but made DETACHING an agent in edit mode a silent no-op: the
      // Agent select was cleared, `posp: ''` was skipped, the PATCH carried no
      // `posp` key at all, and the page still toasted "Policy updated
      // successfully" over a policy whose agent had not moved. An explicit null
      // is the detach instruction the server understands (PolicyViewSet.
      // perform_update), and it is the server — not this line — that refuses
      // the detach when the agent has already been paid.
      if (isEdit && !data.posp) {
        payload.posp = null;
      }
      // Same silent-no-op trap as `posp` above: the loop drops empty values,
      // so clearing the Payment Frequency on an existing installment policy
      // would send no key at all and the page would report "Policy updated"
      // over a schedule that had not moved. Send the clear explicitly.
      if (isEdit && !data.payment_frequency) {
        payload.payment_frequency = '';
        payload.number_of_payments = null;
      }
      // B-01: the per-installment amount belongs to a premium-paying term and
      // nothing else. The prefill keeps the box populated even on a policy
      // paid once (it tracks Total Premium), so it is only sent alongside a
      // frequency. On an edit it is ALWAYS sent -- null when there is no term
      // or the broker blanked it -- so a clear is a clear, not a silent no-op.
      // A blank on CREATE is left out: the server then splits the total, the
      // API's long-standing behaviour.
      if (!data.payment_frequency || !data.installment_amount) {
        if (isEdit) {
          payload.installment_amount = null;
        } else {
          delete payload.installment_amount;
        }
      }
      // M-04, same trap once more. Answering "New — first registration" clears
      // the plate and the registration date in the FORM, but the loop above
      // drops both empties, so on an edit the PATCH carried neither key: the
      // stored policy kept MH01AB1234 and its old registration date while
      // gaining an `rto_id`, under a "Policy updated successfully" toast. That
      // is not cosmetic -- `_motor_facts` would then have two answers for the
      // registering office, and reopening the policy would show the plate
      // back, the RTO block gone and the office riding along invisibly inside
      // `policy_data`. Send the clears the way `posp` and `payment_frequency`
      // above do; both columns take them (vehicle_number is `blank=True`,
      // registration_date `null=True`).
      if (isEdit && isMotorPolicy && submittedAsNewRegistration) {
        payload.vehicle_number = '';
        payload.registration_date = null;
      }
      payload.holder_name = holderName;
      if (useStructuredHolderFields) {
        // Board #272. One nested object, the same shape the Contacts form
        // posts. The server validates it with CustomerCreateSerializer and
        // creates the row inside the policy's transaction -- so a rejected
        // policy leaves no orphan contact, and an accepted one puts a COMPLETE
        // contact in Contacts rather than the placeholder stub the four
        // holder_* fields used to produce.
        const block: Record<string, string> = { customer_type: holderCustomerType };
        newContactKeys(holderCustomerType).forEach((key) => {
          const value = contactValue(key);
          if (value) block[key] = value;
        });
        if (data.holder_phone) block.phone = data.holder_phone;
        if (data.holder_email) block.email = data.holder_email;
        // Customer.full_name is built from first/middle/last, so a company
        // with only `company_name` set would land in Contacts nameless. The
        // Contacts form mirrors the company name into first_name for exactly
        // this reason (see CustomerFormPage.onSubmit); match it.
        if (isCompanyNameType(holderCustomerType) && !block.first_name && block.company_name) {
          block.first_name = block.company_name;
        }
        payload.new_customer = block;
        // The nested block supersedes the flat holder_* stub fields; sending
        // both would give the server two answers to the same question.
        delete payload.holder_salutation;
        delete payload.holder_first_name;
        delete payload.holder_last_name;
      }
      if (!useStructuredHolderFields) {
        // Salutation/first/last only ever feed the auto-created Contact on the
        // new-customer path -- don't carry a stale/default value into a
        // linked-customer or edit-mode request.
        //
        // holder_customer_type is deliberately NOT dropped any more (task 8):
        // it is a Policy column now, and it is the linked-customer path that
        // most needs it -- that is where a Corporate contact's type is copied
        // in from, and where losing it made the reopened form ask a company
        // for its date of birth.
        delete payload.holder_salutation;
        delete payload.holder_first_name;
        delete payload.holder_last_name;
      }

      // Backend requires gross/net/total premium even though the spec marks
      // premium fields optional. Derive sensible defaults so the request
      // succeeds when the user leaves the breakdown empty.
      //
      // CREATE ONLY — and the `isEdit` guard is not cosmetic (B5).
      //
      // `gross_premium` is not a box on this form and never has been, so the
      // fallback below is this page INVENTING a figure the broker never typed.
      // On a create that is the right thing to do: the column is NOT NULL with
      // no default, and on a hand-entered policy gross and total really are
      // equal (this form derives total = net + tax and has no stamp-duty box).
      //
      // On an EDIT it was silent corruption. A quote-converted policy has
      // gross = net + gst and total = gross + stamp_duty (apps/quotes/
      // services.py), so the two DIFFER by the stamp duty — and this line sent
      // `gross_premium: total_premium` on every save, absorbing the stamp duty
      // into gross the moment a broker opened the policy to correct a phone
      // number. Gross is a selectable commission basis, the pro-rata
      // cancellation refund is computed from it, and B5 has just taken it off
      // the detail page, so nothing would have shown the broker it moved.
      //
      // Omitting the key is the honest PATCH: "I have nothing to say about
      // this column." PolicyUpdateSerializer.validate keeps it in step with a
      // total that actually moved, from the stored stamp duty.
      if (!isEdit) {
        if (!('gross_premium' in payload)) {
          payload.gross_premium =
            (payload.total_premium as string | undefined) ||
            (payload.net_premium as string | undefined) ||
            '0';
        }
        if (!('net_premium' in payload)) {
          payload.net_premium = payload.gross_premium;
        }
        if (!('total_premium' in payload)) {
          payload.total_premium = payload.gross_premium;
        }
      }

      // Fold schema-driven type-specific field values into policy_data so
      // non-motor fields and rate dimensions round-trip through the JSON bag.
      const typeSpecific: Record<string, unknown> = {};
      for (const fld of schemaFields) {
        // Master policy number/name are real Policy columns: one source of truth.
        if (fld.section === 'master') continue;
        const v = (data as Record<string, unknown>)[fld.key] ?? rawValues[fld.key];
        if (v !== '' && v !== undefined) typeSpecific[fld.key] = v;
      }
      const premiums: Record<string, unknown> = {};
      for (const b of schema.data?.premiumBases ?? []) {
        const v = (data as Record<string, unknown>)[`premium_${b.code}`];
        if (v !== '' && v !== undefined) premiums[b.code] = v;
      }
      // #332: members ride as a nested replace-the-set list; master policy
      // fields are real columns and only apply to a group structure.
      // A group policy with a master number but no member rows sends no
      // `members` at all (an empty list would log "members changed (0)" on
      // every edit).
      const nothingToSendForMembers = submitMembers.length === 0 && savedMemberCount === 0;
      if (memberStructure && !legacyUntouched && !nothingToSendForMembers) {
        payload.members = membersToPayload(submitMembers, memberStructure);
      }
      if (memberStructure && memberStructure.mode !== 'group') {
        delete payload.master_policy_number;
        delete payload.master_policy_name;
      }

      // #199: `vehicle_condition` has no Policy column and no
      // InsuranceTypeFieldSchema row, so the schema-driven fold above cannot
      // carry it. It goes into policy_data explicitly, and is removed from the
      // top level so it is not posted as a Policy field that does not exist.
      delete payload.vehicle_condition;
      if (isMotorPolicy && data.vehicle_condition) {
        typeSpecific.vehicle_condition = data.vehicle_condition;
      }

      // B-04: Engine No. / Chassis No. -- no Policy column either, so the same
      // explicit fold. The server writes them onto the vehicle master.
      delete payload.engine_number;
      delete payload.chassis_number;
      if (isMotorPolicy) {
        const engine = (data.engine_number || '').trim().toUpperCase();
        const chassis = (data.chassis_number || '').trim().toUpperCase();
        if (engine) typeSpecific.engine_number = engine;
        if (chassis) typeSpecific.chassis_number = chassis;
      }

      // #333: the optional motor extras travel as one `motor_optional` object,
      // blanks dropped. On edit a fully cleared object is still sent, so a
      // cleared box actually clears -- policy_data is replaced wholesale.
      delete payload.motor_optional;
      delete payload.nominee_name;
      delete payload.nominee_relationship;
      delete payload.nominee_share;
      if (isMotorPolicy) {
        const extras: Record<string, string> = {};
        for (const [k, v] of Object.entries(data.motor_optional ?? {})) {
          const text = typeof v === 'string' ? v.trim() : '';
          if (text) extras[k] = text;
        }
        if (Object.keys(extras).length || isEdit) typeSpecific.motor_optional = extras;
        if (!isEdit && data.nominee_name?.trim()) {
          payload.nominees = [
            {
              name: data.nominee_name.trim(),
              relationship: data.nominee_relationship || 'other',
              percentage_share: data.nominee_share || '100',
              is_primary: true,
            },
          ];
        }
      }

      // #332: non-motor nominee (health first; life later). Motor keeps its
      // create-only box above. The update endpoint accepts `nominees` now, so on
      // an edit a cleared box clears the saved nominee rather than being ignored.
      if (!isMotorPolicy && schema.data) {
        if (data.nominee_name?.trim()) {
          payload.nominees = [
            {
              name: data.nominee_name.trim(),
              relationship: data.nominee_relationship || 'other',
              percentage_share: data.nominee_share || '100',
              is_primary: true,
            },
          ];
        } else if (
          isEdit &&
          Array.isArray((existingPolicy as unknown as Record<string, unknown> | undefined)?.nominees) &&
          ((existingPolicy as unknown as { nominees: unknown[] }).nominees.length ?? 0) > 0
        ) {
          payload.nominees = [];
        }
      }

      // M-04: same treatment for the RTO block. `rto_id` IS a declared Motor
      // rate dimension (InsuranceTypeFieldSchema), so the schema-driven fold
      // above would carry it wherever that row exists -- it is folded
      // explicitly all the same, so what the engine matches on never depends
      // on a master-data row being present. State and City are navigation
      // only: the office names its own, and a second copy can only disagree.
      delete payload.rto_id;
      delete payload.rto_state;
      delete payload.rto_city;
      delete payload.rto_pincode;
      if (isMotorPolicy) {
        // Folded whenever the form HOLDS one, not only while the RTO block is
        // on screen. A saved policy that carries both an RTO and a
        // registration date shows the plate boxes (see `isNewRegistration`),
        // and gating the fold on the block being visible would have silently
        // dropped that policy's RTO the first time anyone edited its phone
        // number -- the same erasure cc/gvw were already patched for.
        // Answering "old" clears these four outright (see the select below),
        // which is the one thing that does remove a recorded office.
        if (data.rto_id) typeSpecific.rto_id = data.rto_id;
        if (data.rto_pincode) typeSpecific.rto_pincode = data.rto_pincode;
      }

      if (Object.keys(typeSpecific).length || Object.keys(premiums).length) {
        payload.policy_data = {
          ...((payload.policy_data as Record<string, unknown>) ?? {}),
          ...typeSpecific,
          ...(Object.keys(premiums).length ? { premiums } : {}),
        };
      }

      // B9 (e/f): the vehicle duplicate prompt, and the renewal link. Create
      // only -- `PolicyUpdateSerializer` does not accept the link at all.
      if (!isEdit) {
        const outcome = await runVehicleCheck(data.vehicle_number);
        if (outcome === 'prompted') {
          // The broker's choice in the prompt carries this submit on.
          resumeSubmitRef.current = true;
          return;
        }
        // The number changed under this submit; its check describes another vehicle.
        if (outcome === 'stale') return;
        if (renewalLinkRef.current) {
          payload.previous_policy = renewalLinkRef.current.id;
          payload.is_renewal = true;
          const pairing = `${renewalLinkRef.current.id}:${renewalCustomerKey(payload)}`;
          pendingMismatchKeyRef.current = pairing;
          if (ackMismatchKeyRef.current === pairing) {
            payload.acknowledge_renewal_customer_mismatch = true;
          }
        }
      }

      const savedPolicy = isEdit
        ? await updateMutation.mutateAsync({
            id: editId!,
            data: payload as unknown as Partial<PolicyCreateRequest>,
          })
        : await createMutation.mutateAsync(payload as unknown as PolicyCreateRequest);

      if (policyFile && savedPolicy?.id) {
        try {
          const formData = new FormData();
          formData.append('document_type', uploadDocType);
          formData.append('file', policyFile);
          formData.append('name', policyFile.name);
          await policyService.uploadPolicyDocument(savedPolicy.id, formData);
          // Invalidate the documents cache so the detail page sees the new upload
          // immediately, even within the 5-minute global staleTime window.
          queryClient.invalidateQueries({ queryKey: ['policyDocuments', savedPolicy.id] });
          toast.success('Policy PDF uploaded');
        } catch {
          toast.error(isEdit ? 'Policy saved but PDF upload failed' : 'Policy created but PDF upload failed');
        }
      }
      // Single success message: the create/update mutation's own onSuccess
      // (useCreatePolicy / useUpdatePolicy in usePolicies.ts) already shows
      // exactly one "Policy created/updated successfully" toast. Firing a
      // second one here was the double-toast bug (Task 4.3) -- don't add
      // another. The PDF-upload toast above is a distinct, separate event
      // and only fires when a file was actually uploaded.
      navigate(isEdit ? `/policies/${editId}` : '/policies');
    } catch (err: unknown) {
      // B9: a refused renewal link. `{"previous_policy": [msg], "code": ...}`
      // names no field on this form, so it is handled before the generic
      // mapping (which would also list the bare code as a message).
      const refusal = renewalRefusalOf(err);
      if (refusal) {
        if (
          refusal.code === RENEWAL_CUSTOMER_MISMATCH &&
          ackMismatchKeyRef.current !== pendingMismatchKeyRef.current
        ) {
          // A question, not a no: confirm, then resubmit with the ack.
          setMismatchMessage(refusal.message);
          return;
        }
        setRenewalNotice(refusal.message);
        setServerErrors([refusal.message]);
        return;
      }
      const messages = applyServerErrors(err, setError);
      // B5: `new_customer` is a write-only DictField (not a declared nested
      // serializer field — see PolicyCreateSerializer), so a duplicate-contact
      // 400 on the new-contact block comes back as
      // `{"new_customer": {"email": [...]}}`, which getFieldErrors/
      // applyServerErrors flattens to the key "new_customer.email" — a field
      // name react-hook-form has never heard of, so it never reached the
      // Email/Phone inputs actually on screen (`holder_email`/`holder_phone`).
      // Re-home the two the new-contact form renders.
      const fieldErrors = getFieldErrors(err);
      if (fieldErrors['new_customer.email']) {
        setError('holder_email', { type: 'server', message: fieldErrors['new_customer.email'] });
      }
      if (fieldErrors['new_customer.phone']) {
        setError('holder_phone', { type: 'server', message: fieldErrors['new_customer.phone'] });
      }
      setServerErrors(messages);
      toast.error(messages[0] ?? (isEdit ? 'Failed to update policy' : 'Failed to create policy'));
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // #332: one registry row as a form control. The widget comes from the same
  // `widgetForDataType` the quote forms use, so the two never disagree.
  const renderRegistryField = (f: FieldSchema) => {
    const widget = widgetForDataType(f);
    const message = (errors as Record<string, { message?: string } | undefined>)[f.key]?.message;
    const reg = (register as unknown as (k: string) => Record<string, unknown>)(f.key);
    // An old group policy with no master policy recorded stays saveable until
    // the broker starts filling the master fields in (spec Q8).
    const legacyMasterOptional =
      f.section === 'master' &&
      isEdit &&
      !!existingPolicy &&
      !((existingPolicy as unknown as { members?: unknown[] }).members?.length) &&
      !membersTouched &&
      !watch('master_policy_number') &&
      !watch('master_policy_name');
    const isRequired = f.is_required && !legacyMasterOptional;
    if (widget === 'checkbox') {
      return (
        <label key={f.key} className="flex items-center gap-2 pt-7 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
            {...reg}
          />
          {f.label}
        </label>
      );
    }
    if (widget === 'select') {
      return (
        <Select
          key={f.key}
          label={f.label}
          required={isRequired}
          error={message}
          options={f.is_required ? f.choices : [{ value: '', label: 'None' }, ...f.choices]}
          {...reg}
        />
      );
    }
    return (
      <Input
        key={f.key}
        label={f.label}
        type={widget === 'number' ? 'number' : widget === 'date' ? 'date' : 'text'}
        inputMode={widget === 'number' ? 'decimal' : undefined}
        required={isRequired}
        error={message}
        {...reg}
      />
    );
  };

  // ── B9: the prompt's answers ──────────────────────────────────────────────
  const resumeSubmitIfPending = () => {
    if (!resumeSubmitRef.current) return false;
    resumeSubmitRef.current = false;
    void handleSubmit(onSubmit)();
    return true;
  };

  const handleRenewFromVehicleCheck = (policyId: string) => {
    const match = vehicleCheckResult?.matches.find((m) => m.id === policyId);
    setVehicleCheckResult(null);
    setRenewalNotice('');
    setRenewalLink({ id: policyId, displayNumber: match?.display_policy_number || '' });
    // Mid-submit, the form already holds what the broker typed: carry on.
    // Otherwise fill whatever they have left empty from the renewed policy.
    if (!resumeSubmitIfPending()) void fillFromRenewalSource(policyId, { refuseIneligible: false });
  };

  const handleNewPolicyAnyway = () => {
    clearedVehicleRef.current = normaliseVehicleNumber(getValues('vehicle_number'));
    setVehicleCheckResult(null);
    resumeSubmitIfPending();
  };

  const dismissVehicleCheck = () => {
    // Closing without choosing is not an answer: it asks again at submit.
    setVehicleCheckResult(null);
    resumeSubmitRef.current = false;
  };

  const undoRenewalLink = () => {
    setRenewalLink(null);
    setRenewalNotice('');
    // Undo means "this is new business" -- do not re-ask about the same number.
    clearedVehicleRef.current = normaliseVehicleNumber(getValues('vehicle_number'));
  };

  const confirmCustomerMismatch = () => {
    setMismatchMessage(null);
    ackMismatchKeyRef.current = pendingMismatchKeyRef.current;
    void handleSubmit(onSubmit)();
  };

  const confirmLongTerm = () => {
    if (longTermConfirmMonths === null) return;
    ackLongTermMonthsRef.current = longTermConfirmMonths;
    setLongTermConfirmMonths(null);
    void handleSubmit(onSubmit)();
  };

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/policies" />
      </div>
      <PageHeader
        title={isEdit ? 'Edit Policy' : 'Add Policy'}
        breadcrumbs={[
          { label: 'Policies', href: '/policies' },
          { label: isEdit ? 'Edit Policy' : 'Add Policy' },
        ]}
        actions={
          <Link to="/policies">
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          <ErrorSummary messages={serverErrors} />
          {!!selectedInsuranceType && schema.isLoading && (
            <p role="status" className="text-sm text-gray-500 dark:text-gray-400">
              Loading the form for this insurance type…
            </p>
          )}
          {!!selectedInsuranceType && schema.isError && (
            <div
              role="alert"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
            >
              <span>Could not load the form for this insurance type. Saving is disabled until it loads.</span>
              <Button type="button" variant="outline" size="sm" onClick={() => void schema.refetch()}>
                Retry
              </Button>
            </div>
          )}
          {/* B9 (f): this policy is being recorded as the renewal of another. */}
          {!isEdit && renewalLink && (
            <div
              data-testid="renewal-link-banner"
              className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
            >
              <span>Renewal of {renewalLink.displayNumber || 'the selected policy'}</span>
              <span aria-hidden="true">·</span>
              <button type="button" className="font-medium underline hover:no-underline" onClick={undoRenewalLink}>
                Undo
              </button>
            </div>
          )}
          {!isEdit && renewalNotice && (
            <div
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            >
              {renewalNotice}
            </div>
          )}
          {/* Section 1: Policy Context */}
          <Card>
            <CardHeader title="Policy Context" subtitle="Insurance classification and company" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Task 24: Insurance Type/Policy Type/Group/Subgroup/Insurance
                  Company/Product are all API-backed FK pickers -- converted
                  to SearchableSelect, same as Make/Model/Variant above (T15).
                  This form's validation is entirely resolver/onSubmit-driven
                  (zodResolver(policyCreateSchema) for insurance_type/insurer,
                  the manual classificationInvalid check in onSubmit for
                  policy_type/group/subgroup -- see FIX 11 above), never the
                  browser's native <select required>, so there is no guard to
                  add here -- confirmed before converting, per the Task 6
                  follow-up (5545ad1). Each onChange below reproduces exactly
                  the side effects the old register(name, { onChange }) had:
                  setValue (not register) drives the field since
                  SearchableSelect is value/onChange-controlled. */}
              <SearchableSelect
                id="insurance_type"
                label="Insurance Type"
                options={classification.insuranceType.options}
                value={selectedInsuranceType || ''}
                onChange={(v) => {
                  markProductDimensionTouched();
                  setValue('insurance_type', v, { shouldValidate: true });
                  setValue('policy_type', '');
                  setValue('group', '');
                  setValue('subgroup', '');
                }}
                placeholder={classification.insuranceType.isLoading ? 'Loading...' : 'Select insurance type'}
                error={errors.insurance_type?.message}
                required
                disabled={!!selectedProduct}
              />
              {!selectedInsuranceType && prefillCoverText.insuranceType && (
                <p className="-mt-2 text-xs text-amber-700 dark:text-amber-400">
                  {prefillCoverText.source} recorded: &ldquo;{prefillCoverText.insuranceType}&rdquo; — not
                  matched, please select
                </p>
              )}
              <SearchableSelect
                id="policy_type"
                label="Policy Type"
                options={classification.policyType.options}
                value={selectedPolicyType || ''}
                onChange={(v) => {
                  markProductDimensionTouched();
                  setValue('policy_type', v, { shouldValidate: true });
                }}
                placeholder={classification.policyType.state.placeholder}
                error={errors.policy_type?.message}
                required={classification.policyType.state.required}
                disabled={classification.policyType.state.disabled || !!selectedProduct}
              />
              <SearchableSelect
                id="group"
                label="Group"
                options={classification.group.options}
                value={selectedGroup || ''}
                onChange={(v) => {
                  markProductDimensionTouched();
                  setValue('group', v, { shouldValidate: true });
                  setValue('subgroup', '');
                }}
                placeholder={classification.group.state.placeholder}
                error={errors.group?.message}
                required={classification.group.state.required}
                disabled={classification.group.state.disabled || !!selectedProduct}
              />
              <SearchableSelect
                id="subgroup"
                label="Subgroup"
                options={classification.subgroup.options}
                value={selectedSubgroup || ''}
                onChange={(v) => {
                  markProductDimensionTouched();
                  setValue('subgroup', v, { shouldValidate: true });
                }}
                placeholder={classification.subgroup.state.placeholder}
                error={errors.subgroup?.message}
                required={classification.subgroup.state.required}
                disabled={classification.subgroup.state.disabled || !!selectedProduct}
              />
              <div>
                <SearchableSelect
                  id="insurer"
                  label="Insurance Company"
                  options={insurerOptions}
                  value={selectedInsurer || ''}
                  onChange={(v) => {
                    markProductDimensionTouched();
                    setValue('insurer', v, { shouldValidate: true });
                  }}
                  placeholder={insurersLoading ? 'Loading...' : 'Select insurance company'}
                  error={errors.insurer?.message}
                  required
                />
                {!insurersLoading && insurerOptions.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    No connected insurance companies yet.{' '}
                    <Link to="/broker/insurers" className="font-medium underline">
                      Connect to insurers
                    </Link>{' '}
                    first.
                  </p>
                )}
              </div>
              <SearchableSelect
                id="product"
                label="Product"
                options={productOptions}
                value={selectedProduct || ''}
                onChange={(v) => {
                  setValue('product', v, { shouldValidate: true });
                  // Auto-populate + lock classification from the chosen product.
                  const prod = (products || []).find((p) => p.id === v);
                  if (!prod) {
                    // Cleared: unlock classification for manual entry.
                    return;
                  }
                  if (prod.insurance_type) setValue('insurance_type', prod.insurance_type, { shouldValidate: true });
                  if (prod.policy_type) setValue('policy_type', prod.policy_type, { shouldValidate: true });
                  if (prod.group) setValue('group', prod.group, { shouldValidate: true });
                  if (prod.subgroup) setValue('subgroup', prod.subgroup, { shouldValidate: true });
                }}
                placeholder={
                  !selectedInsuranceType && !selectedInsurer
                    ? 'Select insurance type or company first'
                    : productsLoading
                      ? 'Loading...'
                      : noProductsForCombination
                        ? 'No products for this combination'
                        : 'Select product'
                }
                error={errors.product?.message}
                disabled={!selectedInsuranceType && !selectedInsurer}
                helperText={
                  noProductsForCombination
                    ? 'No products for this combination. Change the insurance type, policy type, group, subgroup or company to see others.'
                    : 'Selecting a product auto-fills and locks its classification'
                }
              />
              <Input
                label="Policy Number"
                placeholder="Enter the policy number"
                required={!isEdit}
                error={errors.insurer_policy_number?.message}
                {...register('insurer_policy_number')}
              />
            </div>
          </Card>

          {/* Section 1b: Customer (optional) */}
          <Card>
            <CardHeader
              title="Customer"
              subtitle="Link an existing customer (optional). Leave blank to enter a new customer's details below."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="w-full">
                {/* Server-driven, same as the Agent picker below and for the
                    same reason: `customerSearch` already narrows this list on
                    the SERVER, and `common/SearchableSelect` would then re-filter
                    the answer client-side on the visible label. A contact matched
                    on a field the label does not show (phone digits the label
                    truncates, or an e-mail) came back from the server and was
                    hidden again, reading as "no matches" for a contact that
                    exists. It also filtered out the "New customer" clear entry
                    while any query was typed, so an accidental link could not be
                    undone without first emptying the box. */}
                {/* ui/SearchableSelect renders only the control, so the label
                    is ours -- same pairing as the Agent picker below. */}
                <label htmlFor="customer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer
                </label>
                <ServerSearchableSelect
                  id="customer"
                  loading={customersLoading}
                  options={[
                    { value: '', label: customersLoading ? 'Loading...' : 'New customer (enter holder details below)' },
                    // Pin the selected customer at the top when it's not in the
                    // current (search-narrowed) slice, so its label still shows.
                    ...(selectedCustomer && !customerList.some((c) => c.id === selectedCustomer.id)
                      ? [
                          {
                            value: selectedCustomer.id,
                            label: [
                              selectedCustomer.full_name ||
                                `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim(),
                              selectedCustomer.phone,
                            ]
                              .filter(Boolean)
                              .join(' — '),
                          },
                        ]
                      : []),
                    ...customerList.map((c: Customer) => ({
                      value: c.id,
                      label: [c.full_name || `${c.first_name} ${c.last_name}`.trim(), c.phone]
                        .filter(Boolean)
                        .join(' — '),
                    })),
                  ]}
                  value={selectedCustomerId || ''}
                  onChange={(val) => setValue('customer', val ?? '')}
                  onQueryChange={setCustomerSearch}
                  placeholder="New customer (enter holder details below)"
                />
                {selectedCustomer && (
                  <p className="mt-2 text-xs text-primary-600 dark:text-primary-400">
                    Linking to{' '}
                    {selectedCustomer.full_name ||
                      `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim()}
                    . Holder fields below are pre-filled — edit if needed.
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Section 2: Policy Holder */}
          <Card>
            <CardHeader
              title="Policy Holder"
              subtitle={
                selectedCustomer
                  ? 'Pre-filled from selected customer'
                  : useStructuredHolderFields
                    ? "Enter the new customer's basic details"
                    : 'Enter the holder details'
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {useStructuredHolderFields ? (
                // Wave E: policy-holder parity with Contacts -- the SAME
                // Basic-Information fields as CustomerFormPage (Wave D1b),
                // trimmed to only the mandatory subset (name + phone + type
                // + salutation; email optional). Creating the policy
                // auto-creates the Contact from exactly these structured
                // fields (backend PolicyCreateSerializer._create_contact_from_holder),
                // not a naive holder_name split.
                <>
                  <Select
                    label="Customer Type"
                    options={customerTypeOptions}
                    error={errors.holder_customer_type?.message}
                    required
                    {...register('holder_customer_type')}
                  />
                  {/* Board #272: the per-type Basic Information set, rendered
                      from the SAME schema the Contacts form uses. */}
                  {basicFieldSpecs.map((spec) => (
                    <NewContactField
                      key={spec.key}
                      spec={spec}
                      value={newContact[spec.key] ?? ''}
                      onChange={(value) => setContactField(spec.key, value)}
                    />
                  ))}
                  <Input
                    label="Holder Phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="e.g. 9876543210"
                    error={errors.holder_phone?.message}
                    required
                    {...register('holder_phone')}
                  />
                  <Input
                    label="Holder Email"
                    type="email"
                    placeholder="email@example.com"
                    error={errors.holder_email?.message}
                    {...register('holder_email')}
                  />
                  {NEW_CONTACT_ADDRESS_FIELDS.filter(
                    (field) => field.key !== 'city' && field.key !== 'state' && field.key !== 'pincode'
                  ).map((field) => (
                    <Input
                      key={field.key}
                      label={field.label}
                      name={`new_customer.${field.key}`}
                      maxLength={field.maxLength}
                      inputMode={field.inputMode}
                      value={newContact[field.key] ?? ''}
                      onChange={(event) => setContactField(field.key, event.target.value)}
                    />
                  ))}
                  {/* B6: PIN pre-fills State (and City); State is always a
                      dropdown. Pulled out of the generic map above because
                      these three need the shared lookup wiring the other
                      new-contact fields don't. */}
                  <AddressFields
                    pincode={newContact.pincode ?? ''}
                    onPincodeChange={(value) => setContactField('pincode', value)}
                    pincodeId="new_customer.pincode"
                    pincodeName="new_customer.pincode"
                    city={newContact.city ?? ''}
                    onCityChange={(value) => setContactField('city', value)}
                    cityId="new_customer.city"
                    cityName="new_customer.city"
                    state={newContact.state ?? ''}
                    onStateChange={(value) => setContactField('state', value)}
                    stateId="new_customer.state"
                    stateName="new_customer.state"
                  />
                  {identityFieldSpecs.map((spec) => (
                    <NewContactField
                      key={spec.key}
                      spec={spec}
                      value={newContact[spec.key] ?? ''}
                      onChange={(value) => setContactField(spec.key, value)}
                    />
                  ))}
                </>
              ) : (
                <>
                  <Input
                    label="Holder Name"
                    placeholder="Full name of policy holder"
                    error={errors.holder_name?.message}
                    required={!selectedCustomer}
                    {...register('holder_name', { onChange: nameOnChange })}
                  />
                  <Input
                    label="Holder Phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="e.g. 9876543210"
                    error={errors.holder_phone?.message}
                    required={!selectedCustomer}
                    {...register('holder_phone')}
                  />
                  <Input
                    label="Holder Email"
                    type="email"
                    placeholder="email@example.com"
                    error={errors.holder_email?.message}
                    {...register('holder_email')}
                  />
                  {/* Task 8: a company has no date of birth or gender. React
                      Hook Form keeps a field's value when its input unmounts
                      (shouldUnregister defaults to false), so hiding these
                      leaves an already-saved value intact rather than silently
                      blanking a column on the next save. */}
                  {isNaturalPersonType(holderCustomerType) && (
                    <>
                      <Input
                        label="Date of Birth"
                        type="date"
                        error={errors.holder_dob?.message}
                        {...register('holder_dob')}
                      />
                      <Select
                        label="Gender"
                        options={[
                          { value: 'male', label: 'Male' },
                          { value: 'female', label: 'Female' },
                          { value: 'other', label: 'Other' },
                        ]}
                        placeholder="Select gender"
                        error={errors.holder_gender?.message}
                        {...register('holder_gender')}
                      />
                    </>
                  )}
                  <Input
                    label="Pincode"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="e.g. 110001"
                    error={errors.holder_pincode?.message}
                    {...register('holder_pincode')}
                  />
                </>
              )}
            </div>
          </Card>

          {/* Section 2b: Vehicle Details — only for motor policies */}
          {isMotorPolicy && (
            <Card>
              <CardHeader title="Vehicle Details" subtitle="Vehicle and registration details (motor policies)" />
              {/* Item 2 -- the prefill did not load. Say so. An empty vehicle
                  block that means "the lookup failed" looks exactly like one
                  that means "this contact has no vehicle on file", and the
                  broker would trust the second reading. */}
              {contactPrefillFailed && (
                <div
                  role="alert"
                  className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                >
                  Could not load this contact&rsquo;s saved vehicle details. Nothing has been
                  filled in for you &mdash; enter the vehicle below, or reload the page to try
                  again.
                </div>
              )}
              {/* Item 2 -- this contact owns more than one vehicle. ASK. The
                  form must not choose, because choosing wrong writes another
                  car's registration onto this policy and looks identical to
                  choosing right. */}
              {contactVehicles.length > 1 && (
                <div className="mb-6">
                  <Select
                    id="contact_vehicle"
                    name="contact_vehicle"
                    label="Vehicle on file"
                    value={chosenContactVehicleId}
                    onChange={(e) => {
                      const picked = contactVehicles.find((v) => v.id === e.target.value);
                      setChosenContactVehicleId(e.target.value);
                      if (picked) applyContactVehicle(picked, true);
                    }}
                    options={[
                      { value: '', label: 'Select which vehicle' },
                      ...contactVehicles.map((v) => ({ value: v.id, label: v.label })),
                    ]}
                    helperText={`This contact has ${contactVehicles.length} vehicles on file. Nothing is filled in until you choose one.`}
                  />
                </div>
              )}
              {/* Where the contact's own record was thin, the shared
                  cross-broker registration trail filled the gaps. That trail
                  can be stale -- a sold car keeps its old row -- so the broker
                  is told where the numbers came from rather than being left to
                  assume their own book. */}
              {chosenVehicleFromTrail && (
                <p className="mb-6 text-xs text-gray-500 dark:text-gray-400">
                  Some details below came from the shared vehicle registration record, not from
                  this contact&rsquo;s own file. Please check them.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* M-04 — a vehicle being registered for the first time has no
                    plate and no registration date yet, so those two boxes are
                    replaced by the RTO address block rather than left there to
                    be filled with something invented. Nothing here is required
                    (decision 13). */}
                {isNewRegistration ? (
                  <RtoAddressFields
                    state={watchedRtoState || ''}
                    onStateChange={(v) => setValue('rto_state', v)}
                    city={watchedRtoCity || ''}
                    onCityChange={(v) => setValue('rto_city', v)}
                    pincode={watchedRtoPincode || ''}
                    onPincodeChange={(v) => setValue('rto_pincode', v)}
                    rto={watchedRtoId || ''}
                    onRtoChange={(v) => setValue('rto_id', v)}
                    pincodeError={errors.rto_pincode?.message}
                    rtoError={errors.rto_id?.message}
                    stateFallbackLabel={savedRto.data?.state_name}
                    cityFallbackLabel={savedRto.data?.city_name || undefined}
                    rtoFallbackLabel={
                      savedRto.data ? `${savedRto.data.code} — ${savedRto.data.name}` : undefined
                    }
                  />
                ) : (
                  <>
                    <Input
                      label="Vehicle Number"
                      placeholder="e.g. DL01AB1234"
                      maxLength={20}
                      error={errors.vehicle_number?.message}
                      helperText={
                        !isEdit && vehicleCheckFailed
                          ? 'Could not check this vehicle for existing policies.'
                          : undefined
                      }
                      {...register('vehicle_number', { onBlur: onVehicleNumberBlur })}
                    />
                    <Input
                      label="Registration Date"
                      type="date"
                      error={errors.registration_date?.message}
                      {...register('registration_date')}
                    />
                  </>
                )}
                <Input
                  label="Manufacture Year"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 2022"
                  error={errors.manufacturing_year?.message}
                  {...register('manufacturing_year')}
                />
                {/* The three cascading selects are value-bound so a saved
                    selection survives its option list loading late (B4).
                    T15: searchable — long Make/Model/Variant lists otherwise
                    force the broker to scroll a plain <select> to find one
                    entry. The onChange handlers below are the USER-driven
                    cascade and must keep clearing the children; setValue
                    (not register) drives the form since SearchableSelect is
                    value/onChange-controlled, same pattern as Customer/Agent
                    below. */}
                <SearchableSelect
                  id="vehicle_make"
                  label="Make"
                  options={makeOptions}
                  value={selectedMake || ''}
                  onChange={(v) => {
                    setValue('vehicle_make', v, { shouldValidate: true });
                    setValue('vehicle_model', '');
                    setValue('vehicle_variant', '');
                  }}
                  placeholder={makesLoading ? 'Loading...' : 'Select make'}
                  error={errors.vehicle_make?.message}
                />
                {/* The lead named a make we could not match to the master.
                    Showing it beats losing it: the broker can see what was
                    recorded while they pick the right entry. */}
                {!selectedMake && prefillVehicleText.make && (
                  <p className="-mt-4 text-xs text-amber-700 dark:text-amber-500">
                    {prefillVehicleText.source} recorded: &ldquo;{prefillVehicleText.make}&rdquo; — not matched, please select
                  </p>
                )}
                <SearchableSelect
                  id="vehicle_model"
                  label="Model"
                  options={modelOptions}
                  value={selectedModel || ''}
                  onChange={(v) => {
                    setValue('vehicle_model', v, { shouldValidate: true });
                    setValue('vehicle_variant', '');
                  }}
                  placeholder={
                    !selectedMake
                      ? 'Select make first'
                      : modelsLoading
                        ? 'Loading...'
                        : 'Select model'
                  }
                  error={errors.vehicle_model?.message}
                  disabled={!selectedMake}
                />
                {!selectedModel && prefillVehicleText.model && (
                  <p className="-mt-4 text-xs text-amber-700 dark:text-amber-500">
                    {prefillVehicleText.source} recorded: &ldquo;{prefillVehicleText.model}&rdquo; — not matched, please select
                  </p>
                )}
                <SearchableSelect
                  id="vehicle_variant"
                  label="Variant"
                  options={variantOptions}
                  value={selectedVariant || ''}
                  onChange={(v) => setValue('vehicle_variant', v, { shouldValidate: true })}
                  placeholder={
                    !selectedModel
                      ? 'Select model first'
                      : variantsLoading
                        ? 'Loading...'
                        : 'Select variant'
                  }
                  error={errors.vehicle_variant?.message}
                  disabled={!selectedModel}
                />
                {!selectedVariant && prefillVehicleText.variant && (
                  <p className="-mt-4 text-xs text-amber-700 dark:text-amber-500">
                    {prefillVehicleText.source} recorded: &ldquo;{prefillVehicleText.variant}&rdquo; — not matched, please
                    select
                  </p>
                )}
                {/* B1: the Country-Admin fuel master, not free text. T24:
                    API-backed FK, converted to SearchableSelect like the
                    three above -- optional field, no required to guard. */}
                <SearchableSelect
                  id="fuel_type"
                  label="Fuel Type"
                  options={fuelTypeOptions}
                  value={selectedFuelType || ''}
                  onChange={(v) => setValue('fuel_type', v, { shouldValidate: true })}
                  placeholder={fuelTypesLoading ? 'Loading...' : 'Select fuel type'}
                  error={errors.fuel_type?.message}
                />
                {/* B2: IDV is motor's coverage amount; non-motor types get
                    "Sum Assured" in the Premium Details card instead. Both
                    write the columns that already existed and already render
                    on the detail page — they were simply never on the form. */}
                <Input
                  label="IDV"
                  type="text"
                  placeholder="0.00"
                  helperText="Insured Declared Value of the vehicle"
                  error={errors.idv?.message}
                  {...register('idv')}
                />
                {/* B-04: the Vehicle Database shows these per vehicle, and on
                    PROD not one vehicle had either -- there was nowhere to type
                    them. Optional; saved into policy_data and copied onto the
                    customer's vehicle by the server. */}
                <Input
                  label="Engine No."
                  type="text"
                  placeholder="As on the RC"
                  helperText="Optional."
                  maxLength={50}
                  error={errors.engine_number?.message}
                  {...register('engine_number')}
                />
                <Input
                  label="Chassis No."
                  type="text"
                  placeholder="As on the RC"
                  helperText="Optional."
                  maxLength={50}
                  error={errors.chassis_number?.message}
                  {...register('chassis_number')}
                />
                {/* Queue #218. Both are driven by the InsuranceTypeFieldSchema
                    registry rather than a hardcoded rule, which is how the rest
                    of this form decides what a type needs: `cc` is scoped to
                    Motor, `gvw` to Motor + Commercial Vehicle (migration 0023),
                    so GVW appears only once that group is chosen and never on a
                    two-wheeler. A second, hardcoded motor/group detection rule
                    is what has caused this class of bug here before. */}
                {schemaOffers('gvw') && (
                  <Input
                    label="GVW (kg)"
                    type="text"
                    placeholder="e.g. 7500"
                    helperText="Gross vehicle weight. Optional."
                    error={errors.gvw?.message}
                    {...register('gvw')}
                  />
                )}
              </div>

              {/* Board #199 — optional fields. None of these is required to save
                  a policy; they exist so a commission rate scoped on CC, NCB,
                  vehicle age or vehicle condition has a value to match against.
                  Left blank they cost nothing, but a rate that DOES name one of
                  them will not match a policy that never recorded it. */}
              <div className="mt-6 border-t border-gray-100 dark:border-gray-700 pt-6">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Optional Fields
                </p>
                <p className="mt-1 mb-4 text-xs text-gray-500 dark:text-gray-400">
                  Not required to save the policy. Commission rates can be scoped on these, and a
                  rate that names one only matches a policy that records it.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Select
                    label="Vehicle Condition"
                    options={vehicleConditionOptions}
                    helperText="Derived from the registration date against the policy start. Change it if the registration says otherwise."
                    error={errors.vehicle_condition?.message}
                    {...register('vehicle_condition', {
                      onChange: (e) => {
                        vehicleConditionEdited.current = true;
                        // M-04: answering "New — first registration" is the
                        // deliberate act that swaps the plate and registration
                        // date for the RTO block above. Clearing them here,
                        // rather than merely hiding them, is what stops a
                        // number typed a moment ago from being submitted out
                        // of a field nobody can see any more. Only a USER
                        // choice does this -- the derivation effect above
                        // never reaches this handler.
                        if (e.target.value === 'new') {
                          setValue('vehicle_number', '');
                          setValue('registration_date', '');
                        } else {
                          // ...and the reverse: a vehicle that is NOT a first
                          // registration has no RTO block, so the office
                          // chosen in it stops being this policy's answer
                          // rather than riding along invisibly into
                          // `policy_data` and the commission match.
                          setValue('rto_id', '');
                          setValue('rto_state', '');
                          setValue('rto_city', '');
                          setValue('rto_pincode', '');
                        }
                      },
                    })}
                  />
                  <Input
                    label="NCB (%)"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 20"
                    helperText="Blank or 0 means no NCB. 20, 50 and so on mean the policy carries one."
                    error={errors.ncb_percentage?.message}
                    {...register('ncb_percentage')}
                  />
                  <Input
                    id="vehicle_age_display"
                    label="Vehicle Age (years)"
                    type="text"
                    readOnly
                    value={vehicleAgeYears === null ? '' : String(vehicleAgeYears)}
                    placeholder={watchedRegistrationDate ? '—' : 'Enter a registration date'}
                    helperText="Computed from the registration date to today; never entered by hand."
                  />
                  {schemaOffers('cc') && (
                    <Input
                      label="Engine CC"
                      type="number"
                      inputMode="numeric"
                      placeholder="e.g. 1197"
                      helperText="Optional"
                      error={errors.cc?.message}
                      {...register('cc')}
                    />
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* #333: optional motor details. Nothing here is required; it is all
              saved in policy_data.motor_optional (nominee: its own row, on
              create only). Shown only for motor, like the Vehicle card above. */}
          {isMotorPolicy && (
            <Card>
              <CardHeader
                title="Additional Motor Details"
                subtitle="Optional. Fill in whatever you have; none of it is needed to save the policy."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Select
                  label="Previous Insurer"
                  options={previousInsurerOptions}
                  placeholder="Select previous insurer"
                  helperText="Same company as this policy: renewal. A different one: rollover."
                  {...register('previous_insurer')}
                />
                <Input
                  label="Previous Policy Number"
                  type="text"
                  maxLength={60}
                  {...register('motor_optional.previous_policy_number')}
                />
                <Input
                  label="Previous Policy Expiry Date"
                  type="date"
                  {...register('motor_optional.previous_expiry_date')}
                />
                <Select
                  label="Claim in Previous Year"
                  options={yesNoOptions}
                  {...register('motor_optional.previous_year_claim')}
                />
                <Input
                  label="NCB Proof / Reserve Reference"
                  type="text"
                  maxLength={150}
                  placeholder="Certificate or reference number"
                  {...register('motor_optional.ncb_proof')}
                />
                <Select
                  label="PA Owner-Driver Cover"
                  options={yesNoOptions}
                  {...register('motor_optional.pa_owner_driver')}
                />
                <Input
                  label="Hypothecation / Financier"
                  type="text"
                  maxLength={255}
                  placeholder="Bank or financier name"
                  {...register('motor_optional.financier_name')}
                />
                <Input
                  label="Add-on Covers"
                  type="text"
                  maxLength={500}
                  placeholder="e.g. Zero Dep, Engine Protect"
                  {...register('motor_optional.addon_covers')}
                />
                <Input
                  label="Seating Capacity"
                  type="text"
                  inputMode="numeric"
                  maxLength={3}
                  error={errors.motor_optional?.seating_capacity?.message}
                  {...register('motor_optional.seating_capacity')}
                />
                <Input
                  label="Discount (%)"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 10"
                  error={errors.motor_optional?.discount_percentage?.message}
                  {...register('motor_optional.discount_percentage')}
                />
                <Input
                  label="Loading (%)"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 5"
                  error={errors.motor_optional?.loading_percentage?.message}
                  {...register('motor_optional.loading_percentage')}
                />
                <Input
                  label="Payment Reference"
                  type="text"
                  maxLength={100}
                  placeholder="Transaction / cheque number"
                  {...register('motor_optional.payment_reference')}
                />
              </div>
              {!isEdit && (
                <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
                  <p className="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300">Nominee</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Input
                      label="Nominee Name"
                      type="text"
                      maxLength={255}
                      error={errors.nominee_name?.message}
                      {...register('nominee_name')}
                    />
                    <Select
                      label="Nominee Relationship"
                      options={nomineeRelationshipOptions}
                      {...register('nominee_relationship')}
                    />
                    <Input
                      label="Nominee Share (%)"
                      type="text"
                      inputMode="decimal"
                      placeholder="100"
                      error={errors.nominee_share?.message}
                      {...register('nominee_share')}
                    />
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* #332: health. The shape of this block follows the product's member
              structure (resolved server-side), and the field cards are rendered
              from the InsuranceTypeFieldSchema registry. */}
          {memberStructure && masterFields.length > 0 && (
            <Card>
              <CardHeader title="Master Policy" subtitle="The group policy this certificate sits under" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {masterFields.map(renderRegistryField)}
              </div>
            </Card>
          )}
          {memberStructure && (
            <InsuredMembersBlock
              structure={memberStructure}
              members={members}
              onChange={(rows) => {
                setMembers(rows);
                setMembersTouched(true);
                setMemberErrors({});
              }}
              errors={memberErrors}
              fieldsRequired={
                !(
                  isEdit &&
                  !!existingPolicy &&
                  !((existingPolicy as unknown as { members?: unknown[] }).members?.length) &&
                  !membersTouched
                )
              }
            />
          )}
          {memberStructure && coverageFields.length > 0 && (
            <Card>
              <CardHeader title="Cover Details" subtitle="Optional unless marked required" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {coverageFields.map(renderRegistryField)}
              </div>
            </Card>
          )}
          {/* #332: nominee for every non-motor type (motor keeps its own box in
              the Additional Motor Details card). Optional; one nominee. */}
          {!isMotorPolicy && schema.data && (
            <Card>
              <CardHeader title="Nominee" subtitle="Optional" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Input
                  label="Nominee Name"
                  type="text"
                  maxLength={255}
                  error={errors.nominee_name?.message}
                  {...register('nominee_name')}
                />
                <Select
                  label="Nominee Relationship"
                  options={nomineeRelationshipOptions}
                  {...register('nominee_relationship')}
                />
                <Input
                  label="Nominee Share (%)"
                  type="text"
                  inputMode="decimal"
                  placeholder="100"
                  error={errors.nominee_share?.message}
                  {...register('nominee_share')}
                />
              </div>
            </Card>
          )}

          {/* Section 3: Coverage Period */}
          <Card>
            <CardHeader title="Coverage Period" subtitle="Policy dates and term" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Start Date"
                type="date"
                error={errors.inception_date?.message}
                required
                {...register('inception_date')}
              />
              <Input
                label="Expiry Date"
                type="date"
                error={errors.expiry_date?.message}
                required
                {...register('expiry_date')}
              />
              <Input
                label="Issue Date"
                type="date"
                error={errors.issue_date?.message}
                {...register('issue_date')}
              />
              {isGracePeriodType && (
                <Input
                  label="Grace Period"
                  type="date"
                  error={errors.grace_period?.message}
                  {...register('grace_period')}
                />
              )}
              <Input
                label="Policy Term (months)"
                type="number"
                placeholder="e.g. 12"
                error={errors.policy_term_months?.message}
                {...register('policy_term_months')}
              />
            </div>

            {/* Premium Paying Term. Life/Health/Term policies are paid over
                many installments against ONE policy with ONE policy number --
                a renewal here settles the next installment rather than
                creating a second policy. Both fields are optional: leave them
                blank for a policy paid once. */}
            {supportsInstallments && (
              <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Premium Paying Term
                </h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Optional. Set both to pay this policy in installments — the policy number
                  stays the same for the whole term, and each payment is tracked against it.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <Select
                    label="Payment Frequency"
                    options={[
                      { value: '', label: 'Paid once (no installments)' },
                      ...PAYMENT_FREQUENCY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
                    ]}
                    error={errors.payment_frequency?.message}
                    {...register('payment_frequency')}
                  />
                  <Input
                    label="No. of Payments"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_NUMBER_OF_PAYMENTS}
                    placeholder={`1-${MAX_NUMBER_OF_PAYMENTS}`}
                    helperText="How many installments in total, not years."
                    error={errors.number_of_payments?.message}
                    {...register('number_of_payments')}
                  />
                  <Input
                    id="installment_amount"
                    label="Installment Amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    helperText="Premium per installment. Prefilled from Total Premium; clear it to split the total across the payments."
                    error={errors.installment_amount?.message}
                    {...register('installment_amount')}
                  />
                  <Input
                    id="premium_paying_term_end"
                    label="Premium Paying Term ends"
                    type="text"
                    readOnly
                    value={premiumPayingTermEnd ?? ''}
                    placeholder={
                      watchedPaymentFrequency && watchedNumberOfPayments
                        ? 'Enter a start date'
                        : 'Set frequency and payments'
                    }
                    helperText="Derived from the start date, frequency and payment count. Never entered by hand."
                  />
                </div>
              </div>
            )}
          </Card>

          {/* Section 4: Premium Details */}
          <Card>
            <CardHeader title="Premium Details" subtitle="Optional — enter premium breakdown" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Item 11 — the OD/TP split is motor's alone. Gated on the SAME
                  data-driven `isMotorPolicy` the Vehicle Details card and Sum
                  Assured below already use (derived from
                  InsuranceTypeFieldSchema); a second, hardcoded motor rule is
                  what has caused this class of bug here before. A health or
                  life policy has no own-damage and no third-party leg, so
                  those four boxes were four ways to be confused. */}
              {isMotorPolicy && (
                <>
                  <Input
                    label="OD Premium (without tax)"
                    type="text"
                    placeholder="0.00"
                    error={errors.od_premium?.message}
                    {...register('od_premium')}
                  />
                  <Input
                    label="OD Tax"
                    type="text"
                    placeholder="0.00"
                    error={errors.od_tax?.message}
                    {...register('od_tax')}
                  />
                  <Input
                    label="TP Premium (without tax)"
                    type="text"
                    placeholder="0.00"
                    error={errors.tp_premium?.message}
                    {...register('tp_premium')}
                  />
                  <Input
                    label="TP Tax"
                    type="text"
                    placeholder="0.00"
                    error={errors.tp_tax?.message}
                    {...register('tp_tax')}
                  />
                </>
              )}
              {/* Net Premium is DERIVED for motor (OD + TP) and TYPED for
                  everything else, which is why the same field is read-only in
                  one branch and an input in the other. Non-motor business has
                  no components to add up — the broker has one net figure off
                  the insurer's schedule. */}
              {isMotorPolicy ? (
                <Input
                  label="Net Premium"
                  type="text"
                  name="net_premium"
                  value={watch('net_premium') || ''}
                  disabled
                  readOnly
                  helperText="Auto-calculated: OD Premium + TP Premium"
                  error={errors.net_premium?.message}
                />
              ) : (
                <Input
                  label="Net Premium"
                  type="text"
                  placeholder="0.00"
                  helperText="Premium before taxes"
                  error={errors.net_premium?.message}
                  {...register('net_premium')}
                />
              )}
              {/* Item 11 — Taxes. Bound to the existing `gst_amount` column
                  (see the zod schema). Motor already itemises its tax as OD Tax
                  + TP Tax above and must not gain a second, competing total. */}
              {!isMotorPolicy && (
                <Input
                  label="Taxes"
                  type="text"
                  placeholder="0.00"
                  helperText="GST and any other taxes on this premium"
                  error={errors.gst_amount?.message}
                  {...register('gst_amount')}
                />
              )}
              <Input
                label="Total Premium"
                type="text"
                name="total_premium"
                value={watch('total_premium') || ''}
                disabled
                readOnly
                helperText={
                  isMotorPolicy
                    ? 'Auto-calculated: Net Premium + OD Tax + TP Tax'
                    : 'Auto-calculated: Net Premium + Taxes'
                }
                error={errors.total_premium?.message}
              />
              {/* B2: the non-motor half of the coverage amount. Gated on the
                  SAME isMotorPolicy flag as the Vehicle Details card's IDV
                  (derived from InsuranceTypeFieldSchema), so exactly one of
                  the two is ever on screen — a second motor-detection rule is
                  what has caused this class of bug before. */}
              {!isMotorPolicy && (
                <Input
                  label="Sum Assured"
                  type="text"
                  placeholder="0.00"
                  helperText="Total cover amount for this policy"
                  error={errors.sum_insured?.message}
                  {...register('sum_insured')}
                />
              )}
            </div>
          </Card>

          {/* Section 5: Agent — mandatory only for motor policies */}
          <Card>
            <CardHeader
              title="Agent"
                subtitle="Assign the POSP/agent who sold this policy (optional)"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                {/* ui/SearchableSelect renders only the control, so the label
                    is ours. `htmlFor` + the trigger's `id` is the same pairing
                    common/SearchableSelect does internally — a <button> is a
                    labelable element, so getByLabelText still resolves it. */}
                <label htmlFor="posp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent (POSP)
                </label>
                <ServerSearchableSelect
                  id="posp"
                  options={pospOptions}
                  value={selectedPospId}
                  // Server-driven mode reports the clear entry as '', and the
                  // prop type allows null; the form field is a plain string.
                  onChange={(v) => {
                    const next = v ?? '';
                    setValue('posp', next, { shouldValidate: true });
                    // Pin the label from the list the user actually clicked
                    // in, not from a later fetch. Selecting also closes the
                    // popup, which resets the search term — so an agent that
                    // was only reachable BY searching (page 2+, or a hit on
                    // user__email) is absent from the very next result, and
                    // the pin effect above would never get a chance to see
                    // it: the field would fall back to "Saved selection"
                    // the instant it was chosen.
                    const picked = pospOptions.find((o) => o.value === next);
                    setPinnedPosp(next && picked ? { value: next, label: picked.label } : null);
                  }}
                  onQueryChange={setPospSearch}
                  loading={pospsLoading}
                  placeholder="Search and select agent"
                />
                {errors.posp?.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.posp.message}</p>
                )}
                {/* "Onboard a POSP first" is about having NO agents at all, so
                    it must not fire on a search that simply matched nothing --
                    and it has to test the fetched rows, not pospOptions, which
                    always carries at least the "Select Agent" entry. */}
                {!pospsLoading && !pospSearch && (posps || []).length === 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    No active POSPs yet.{' '}
                    <Link to="/posps" className="font-medium underline">
                      Onboard a POSP
                    </Link>{' '}
                    first.
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Section 6: Policy Document Upload */}
          <Card>
            <CardHeader title="Policy Document" subtitle="Upload the policy PDF copy" />
            <div className="space-y-4">
              {isEdit && (existingDocs?.results?.length ?? 0) > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Uploaded documents
                  </p>
                  {existingDocs!.results.map((d) => (
                    <div key={d.id} className="px-3 py-2 flex items-center justify-between text-sm">
                      <span className="text-gray-900 dark:text-gray-100">{d.name}</span>
                      <span className="text-xs text-gray-500">{d.document_type}</span>
                    </div>
                  ))}
                </div>
              )}
              <SearchableSelect
                label="Document Type"
                value={uploadDocType}
                onChange={setUploadDocType}
                options={[
                  { value: 'policy_copy', label: 'Policy Copy' },
                  // Value must be `endorsement`, not `endorsement_copy`: the
                  // serializer is a ChoiceField over PolicyDocument's own
                  // choices, which never had an `endorsement_copy` member. The
                  // mismatch 400'd every endorsement upload while Policy Copy
                  // and Other worked -- reported as "only Policy Copy is
                  // supported". The label is what brokers read; the value is
                  // the contract.
                  { value: 'endorsement', label: 'Endorsement Copy' },
                  { value: 'other', label: 'Other Document' },
                ]}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select File
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setPolicyFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-gray-600 dark:file:text-gray-200"
                  />
                  {policyFile && (
                    <span className="text-sm text-green-600 dark:text-green-400 whitespace-nowrap">
                      {policyFile.name}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Accepted: PDF, JPG, PNG</p>
              </div>
            </div>
          </Card>

          {/* Section 7: Notes */}
          <Card>
            <CardHeader title="Additional Notes" />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                rows={4}
                placeholder="Any additional notes about this policy..."
                {...register('notes')}
              />
              {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>}
            </div>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to="/policies">
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={schemaPending}
              isLoading={isEdit ? updateMutation.isPending : createMutation.isPending}
            >
              {isEdit ? 'Save Changes' : 'Add Policy'}
            </Button>
          </div>
        </div>
      </form>

      {vehicleCheckResult && (
        <VehicleDuplicateModal
          result={vehicleCheckResult}
          onRenew={handleRenewFromVehicleCheck}
          onNewAnyway={handleNewPolicyAnyway}
          onClose={dismissVehicleCheck}
        />
      )}

      <Modal isOpen={!!mismatchMessage} onClose={() => setMismatchMessage(null)} title="Different customer">
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">{mismatchMessage}</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">Record it as the renewal anyway?</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setMismatchMessage(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmCustomerMismatch}>
              Link as renewal anyway
            </Button>
          </div>
        </div>
      </Modal>

      {/* B12: an unusually long policy term (see LONG_POLICY_TERM_MONTHS) is
          confirmed, never silently blocked -- long terms are legitimate
          (multi-year bundled covers, some life/health products), just rare
          enough that a typo (13 instead of 1, or a stray zero) is worth
          catching before it reaches the insurer. */}
      <Modal
        isOpen={longTermConfirmMonths !== null}
        onClose={() => setLongTermConfirmMonths(null)}
        title="Unusually long policy term"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            This policy term is {longTermConfirmMonths} months (
            {longTermConfirmMonths ? (parseInt(longTermConfirmMonths, 10) / 12).toFixed(1) : ''} years) —
            unusually long for most covers. Long terms are legitimate (a bundled
            multi-year policy, for example), so double-check the number is
            correct before continuing.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setLongTermConfirmMonths(null)}>
              Let me check
            </Button>
            <Button type="button" onClick={confirmLongTerm}>
              Yes, this term is correct
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

type VehicleCheckOutcome = 'clear' | 'prompted' | 'failed' | 'stale';

/**
 * B9 -- who a renewal-link acknowledgement is about: the linked contact, or,
 * for a new contact, the holder identity being posted. Any change asks again.
 */
function renewalCustomerKey(payload: Record<string, unknown>): string {
  return [payload.customer, payload.holder_name, payload.holder_phone, payload.holder_email]
    .map((value) => (value === undefined || value === null ? '' : String(value)))
    .join('|');
}

/**
 * One field of the nested `new_customer` block (board #272).
 *
 * The spec -- label, input type, required flag, options, maxLength -- comes
 * from `utils/customerBasicInfo`, which is also what the Contacts form renders
 * from. Nothing about the field is decided here, so the two forms cannot ask
 * for different things.
 */
function NewContactField({
  spec,
  value,
  onChange,
}: {
  spec: CustomerFieldSpec;
  value: string;
  onChange: (value: string) => void;
}) {
  if (spec.inputType === 'select') {
    return (
      <Select
        label={spec.label}
        // `id` (and therefore the label's htmlFor) falls back to `name`, so
        // without this every generated field would be an unlabelled input.
        name={`new_customer.${spec.key}`}
        options={spec.options ?? []}
        placeholder="Select..."
        required={spec.required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <Input
      label={spec.label}
      name={`new_customer.${spec.key}`}
      type={spec.inputType === 'number' ? 'number' : spec.inputType === 'date' ? 'date' : 'text'}
      placeholder={spec.placeholder}
      maxLength={spec.maxLength}
      inputMode={spec.inputMode}
      min={spec.min}
      step={spec.step}
      required={spec.required}
      value={value}
      onChange={(event) => {
        // `digitsOnly` (aadhaar) rejects non-digit keystrokes rather than
        // silently accepting a value the server will refuse.
        const next = spec.digitsOnly ? event.target.value.replace(/\D/g, '') : event.target.value;
        onChange(next);
      }}
    />
  );
}
